import { AbortError, AbortablePromise, CONTINUOUS_RENDER_POLICY, DEFAULT_PDF_SAVE_MODE, EngineError, EngineErrorCode, MissingIdentity, PermissionDenied, checkAnyCapability, checkCapability, checkCollab, checkSetGroup, createPageImageHandle, decodePdfBits, deserializeError, expandRawScope, normalizeAnnotationDraft, normalizeAnnotationPatch, normalizeAttachmentFileSource, parseScope, passwordPromptFromState, securityStateFromProbe, serializeError, validateScopeArray, wirePack } from "@embedpdf/engine-core/runtime";
import { createPdfRuntime } from "@embedpdf/engine-runtime";
import { EventHub, SessionEventPublisher, WorkerHost, generateUuid } from "@embedpdf/engine-services";
//#region src/worker/Priority.ts
/**
* Priority levels for the WorkerQueue. Higher number = served first.
* Wider range than v2 to leave room for future additions (idle, foreground,
* critical) without re-indexing.
*/
const Priority = {
	LOW: 0,
	MEDIUM: 100,
	HIGH: 200,
	CRITICAL: 300
};
//#endregion
//#region src/document/LocalDocumentActionsService.ts
var LocalDocumentActionsService = class {
	constructor(docId, queue, view, guard) {
		this.docId = docId;
		this.queue = queue;
		this.view = view;
		this.guard = guard;
	}
	read() {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.open");
		} catch (error) {
			return AbortablePromise.rejectReason(error);
		}
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "actions.read",
			jobId,
			docId: this.docId
		}) }, { priority: Priority.MEDIUM });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "actions.read") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			return payload.snapshot;
		});
	}
};
//#endregion
//#region \0@oxc-project+runtime@0.140.0/helpers/esm/typeof.js
function _typeof(o) {
	"@babel/helpers - typeof";
	return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function(o) {
		return typeof o;
	} : function(o) {
		return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o;
	}, _typeof(o);
}
//#endregion
//#region \0@oxc-project+runtime@0.140.0/helpers/esm/toPrimitive.js
function toPrimitive(t, r) {
	if ("object" != _typeof(t) || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != _typeof(i)) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
//#endregion
//#region \0@oxc-project+runtime@0.140.0/helpers/esm/toPropertyKey.js
function toPropertyKey(t) {
	var i = toPrimitive(t, "string");
	return "symbol" == _typeof(i) ? i : i + "";
}
//#endregion
//#region \0@oxc-project+runtime@0.140.0/helpers/esm/defineProperty.js
function _defineProperty(e, r, t) {
	return (r = toPropertyKey(r)) in e ? Object.defineProperty(e, r, {
		value: t,
		enumerable: !0,
		configurable: !0,
		writable: !0
	}) : e[r] = t, e;
}
//#endregion
//#region src/document/LocalDocumentAnnotationsService.ts
/**
* Document-scoped annotation reads, dispatched through the same
* WorkerQueue every other local read uses. The worker host fans out to
* `RawAnnotationReader.listAll` / `RawAnnotationReader.listOne`.
*/
var LocalDocumentAnnotationsService = class {
	constructor(docId, queue, view, guard) {
		this.docId = docId;
		this.queue = queue;
		this.view = view;
		this.guard = guard;
	}
	listRawAll() {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.annotate.read");
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "annotations.listRawAll",
			jobId,
			docId
		}) }, { priority: Priority.MEDIUM });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "annotations.listRawAll") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			return payload.snapshot;
		});
	}
	listRaw(pageObjectNumber) {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.annotate.read");
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "annotations.listRawPage",
			jobId,
			docId,
			pageObjectNumber
		}) }, { priority: Priority.MEDIUM });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "annotations.listRawPage") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			return payload.snapshot;
		});
	}
	beginWeakEdit(pageObjectNumbers) {
		const session = new LocalWeakAnnotationEditSession(pageObjectNumbers);
		return AbortablePromise.resolveValue(session);
	}
};
var LocalWeakAnnotationEditSession = class {
	constructor(pageObjectNumbers) {
		_defineProperty(this, "id", "local-noop");
		_defineProperty(this, "expiresAt", Number.MAX_SAFE_INTEGER);
		_defineProperty(this, "heartbeatIntervalMs", Number.MAX_SAFE_INTEGER);
		_defineProperty(this, "pages", void 0);
		this.pages = [...pageObjectNumbers];
	}
	get pageObjectNumbers() {
		return this.pages;
	}
	covers(pageObjectNumber) {
		return this.pages.includes(pageObjectNumber);
	}
	updatePages(pageObjectNumbers) {
		this.pages = [...pageObjectNumbers];
		return AbortablePromise.resolveValue(void 0);
	}
	heartbeat() {
		return AbortablePromise.resolveValue(void 0);
	}
	release() {
		this.pages = [];
		return AbortablePromise.resolveValue(void 0);
	}
};
//#endregion
//#region src/document/LocalDocumentAttachmentsService.ts
/**
* Document-level attachments (the catalog's `/EmbeddedFiles` name tree)
* for the local engine. `list()`/`download()` are pure reads over the
* worker session; `create()`/`delete()` are mutations.
*
* Gating mirrors the metadata/bytes tier split the cloud routes use:
* `list()` (metadata only) rides the base `doc.open` read; `download()`
* egresses content bytes, so it gates on `doc.download` — the same rule
* as `pages.extract` and the whole-document download. `create`/`delete`
* are mutations gated on `doc.attachments.modify` and publish
* `attachment.created`/`attachment.deleted` events after the worker
* confirms — ground truth, never optimistic.
*/
var LocalDocumentAttachmentsService = class {
	constructor(docId, queue, view, guard, publisher) {
		this.docId = docId;
		this.queue = queue;
		this.view = view;
		this.guard = guard;
		this.publisher = publisher;
	}
	list() {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.open");
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "attachments.list",
			jobId,
			docId
		}) }, { priority: Priority.MEDIUM });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "attachments.list") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			return payload.items;
		});
	}
	download(ref) {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.download");
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "attachments.readFile",
			jobId,
			docId,
			ref
		}) }, { priority: Priority.MEDIUM });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "attachments.readFile") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			const { content } = payload;
			if (content.bytes === void 0) throw new EngineError(EngineErrorCode.WireFormat, "attachments.readFile returned no bytes (path mode is server-only)");
			return {
				bytes: new Uint8Array(content.bytes),
				name: content.name,
				...content.mimeType !== void 0 ? { mimeType: content.mimeType } : {}
			};
		});
	}
	create(file) {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.attachments.modify");
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const docId = this.docId;
		return AbortablePromise.run(async (signal) => {
			const { wireFile, resource } = await normalizeAttachmentFileSource(file, "r0");
			const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
				kind: "attachments.create",
				jobId,
				docId,
				file: wireFile,
				resources: { r0: resource }
			}, [resource.bytes]) }, { priority: Priority.MEDIUM });
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "attachments.create") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			this.publisher.publishLocal({
				type: "attachment.created",
				...payload.result
			});
			return payload.result;
		});
	}
	delete(ref) {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.attachments.modify");
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "attachments.delete",
			jobId,
			docId,
			ref
		}) }, { priority: Priority.MEDIUM });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "attachments.delete") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			this.publisher.publishLocal({
				type: "attachment.deleted",
				...payload.result
			});
			return payload.result;
		});
	}
};
//#endregion
//#region src/document/LocalDocumentFormsService.ts
/**
* Document-scoped forms service. Reads gate on `doc.forms.read`, value
* writes and imports on `doc.forms.fill`, repair on `doc.forms.modify` —
* cloud parity with the layer form routes. The worker host fans out to
* `FormReader` / `FormMutator`, which serve every read from the session's
* version-keyed form-model cache.
*/
var LocalDocumentFormsService = class {
	constructor(docId, queue, view, guard, publisher) {
		this.docId = docId;
		this.queue = queue;
		this.view = view;
		this.guard = guard;
		this.publisher = publisher;
	}
	list() {
		const rejected = this.gate("doc.forms.read");
		if (rejected) return rejected;
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "forms.list",
			jobId,
			docId
		}) }, { priority: Priority.MEDIUM });
		return this.await(submission, "forms.list", (payload) => payload.snapshot);
	}
	get(ref) {
		return AbortablePromise.run(async (signal) => {
			const field = (await this.forwardAbort(this.list(), signal)).fields.find((f) => ref.kind === "objectNumber" ? f.fieldObjectNumber === ref.fieldObjectNumber : f.name === ref.name);
			if (!field) throw new EngineError(EngineErrorCode.NotFound, ref.kind === "objectNumber" ? `form field not found: object ${ref.fieldObjectNumber}` : `form field not found: "${ref.name}"`);
			return field;
		});
	}
	setValue(ref, value) {
		const rejected = this.gate("doc.forms.fill");
		if (rejected) return rejected;
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "forms.setValue",
			jobId,
			docId,
			ref,
			value
		}) }, { priority: Priority.HIGH });
		return this.await(submission, "forms.setValue", (payload) => {
			this.publisher.publishLocal({
				type: "form.valueChanged",
				...payload.result
			});
			return payload.result;
		});
	}
	reset(ref) {
		const rejected = this.gate("doc.forms.fill");
		if (rejected) return rejected;
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "forms.reset",
			jobId,
			docId,
			ref
		}) }, { priority: Priority.HIGH });
		return this.await(submission, "forms.reset", (payload) => {
			this.publisher.publishLocal({
				type: "form.valueChanged",
				...payload.result
			});
			return payload.result;
		});
	}
	applyEffects(effects) {
		const rejected = this.gate("doc.forms.fill");
		if (rejected) return rejected;
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "forms.applyEffects",
			jobId,
			docId,
			effects
		}) }, { priority: Priority.HIGH });
		return this.await(submission, "forms.applyEffects", (payload) => {
			if (payload.result.meta !== null) this.publisher.publishLocal({
				type: "form.effectsApplied",
				...payload.result
			});
			return payload.result;
		});
	}
	exportData(format = "xfdf") {
		const rejected = this.gate("doc.forms.read");
		if (rejected) return rejected;
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "forms.export",
			jobId,
			docId,
			format
		}) }, { priority: Priority.MEDIUM });
		return this.await(submission, "forms.export", (payload) => ({
			format: payload.format,
			bytes: new Uint8Array(payload.bytes)
		}));
	}
	importData(data, format) {
		const rejected = this.gate("doc.forms.fill");
		if (rejected) return rejected;
		const docId = this.docId;
		const buffer = toOwnedArrayBuffer(data);
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "forms.import",
			jobId,
			docId,
			data: buffer,
			...format ? { format } : {}
		}, [buffer]) }, { priority: Priority.HIGH });
		return this.await(submission, "forms.import", (payload) => {
			this.publisher.publishLocal({
				type: "form.imported",
				...payload.result
			});
			return payload.result;
		});
	}
	createField(draft) {
		const rejected = this.gate("doc.forms.modify");
		if (rejected) return rejected;
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "forms.createField",
			jobId,
			docId,
			draft
		}) }, { priority: Priority.HIGH });
		return this.await(submission, "forms.createField", (payload) => {
			this.publisher.publishLocal({
				type: "form.fieldCreated",
				...payload.result
			});
			return payload.result;
		});
	}
	updateField(ref, patch) {
		const rejected = this.gate("doc.forms.modify");
		if (rejected) return rejected;
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "forms.updateField",
			jobId,
			docId,
			ref,
			patch
		}) }, { priority: Priority.HIGH });
		return this.await(submission, "forms.updateField", (payload) => {
			this.publisher.publishLocal({
				type: "form.fieldUpdated",
				...payload.result
			});
			return payload.result;
		});
	}
	deleteField(ref) {
		const rejected = this.gate("doc.forms.modify");
		if (rejected) return rejected;
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "forms.deleteField",
			jobId,
			docId,
			ref
		}) }, { priority: Priority.HIGH });
		return this.await(submission, "forms.deleteField", (payload) => {
			this.publisher.publishLocal({
				type: "form.fieldDeleted",
				...payload.result
			});
			return payload.result;
		});
	}
	attachWidget(ref, widget, options) {
		const rejected = this.gate("doc.forms.modify");
		if (rejected) return rejected;
		const docId = this.docId;
		const onState = options?.onState;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "forms.attachWidget",
			jobId,
			docId,
			ref,
			widget,
			...onState ? { onState } : {}
		}) }, { priority: Priority.HIGH });
		return this.await(submission, "forms.attachWidget", (payload) => {
			this.publisher.publishLocal({
				type: "form.widgetAttached",
				...payload.result
			});
			return payload.result;
		});
	}
	detachWidget(ref, widget) {
		const rejected = this.gate("doc.forms.modify");
		if (rejected) return rejected;
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "forms.detachWidget",
			jobId,
			docId,
			ref,
			widget
		}) }, { priority: Priority.HIGH });
		return this.await(submission, "forms.detachWidget", (payload) => {
			this.publisher.publishLocal({
				type: "form.widgetDetached",
				...payload.result
			});
			return payload.result;
		});
	}
	repair(options) {
		const rejected = this.gate("doc.forms.modify");
		if (rejected) return rejected;
		const docId = this.docId;
		const bakeAppearances = options?.bakeAppearances ?? false;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "forms.repair",
			jobId,
			docId,
			bakeAppearances
		}) }, { priority: Priority.HIGH });
		return this.await(submission, "forms.repair", (payload) => {
			this.publisher.publishLocal({
				type: "form.repaired",
				...payload.result
			});
			return payload.result;
		});
	}
	gate(cap) {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability(cap);
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		return null;
	}
	await(submission, tag, map) {
		return AbortablePromise.run(async (signal) => {
			const payload = await this.forwardAbort(submission, signal);
			if (payload.tag !== tag) throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			return map(payload);
		});
	}
	forwardAbort(promise, signal) {
		const onAbort = () => promise.abort(signal.reason);
		if (signal.aborted) onAbort();
		else signal.addEventListener("abort", onAbort, { once: true });
		return promise;
	}
};
function toOwnedArrayBuffer(data) {
	if (data instanceof ArrayBuffer) return data.slice(0);
	const copy = new ArrayBuffer(data.byteLength);
	new Uint8Array(copy).set(data);
	return copy;
}
//#endregion
//#region src/document/LocalDocumentPagesService.ts
/** Detach a Uint8Array view into a standalone, transferable ArrayBuffer. */
function copyToExactBuffer(view) {
	const copy = new ArrayBuffer(view.byteLength);
	new Uint8Array(copy).set(view);
	return copy;
}
/**
* Document-scoped page service for the local engine. All work funnels
* through the same in-process worker the rest of the engine uses, so
* `pages.move()` is sequenced against any in-flight annotation
* mutations: a page reorder cannot land while a write to one of those
* pages is mid-flight, and the reorder is observed atomically by every
* subsequent read.
*/
var LocalDocumentPagesService = class {
	constructor(docId, queue, view, guard, publisher) {
		this.docId = docId;
		this.queue = queue;
		this.view = view;
		this.guard = guard;
		this.publisher = publisher;
	}
	list() {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.open");
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "pages.list",
			jobId,
			docId
		}) }, { priority: Priority.MEDIUM });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "pages.list") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			return payload.snapshot;
		});
	}
	move(pageObjectNumbers, destIndex) {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.pages.assemble");
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "pages.move",
			jobId,
			docId,
			pageObjectNumbers,
			destIndex
		}) }, { priority: Priority.HIGH });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "pages.move") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			this.publisher.publishLocal({
				type: "pages.moved",
				pageObjectNumbers,
				destIndex,
				...payload.result
			});
			return payload.result;
		});
	}
	rotate(pageObjectNumbers, rotation) {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.pages.assemble");
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "pages.rotate",
			jobId,
			docId,
			pageObjectNumbers,
			rotation
		}) }, { priority: Priority.HIGH });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "pages.rotate") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			this.publisher.publishLocal({
				type: "pages.rotated",
				pageObjectNumbers,
				rotation,
				...payload.result
			});
			return payload.result;
		});
	}
	delete(pageObjectNumbers) {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.pages.assemble");
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "pages.delete",
			jobId,
			docId,
			pageObjectNumbers
		}) }, { priority: Priority.HIGH });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "pages.delete") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			this.publisher.publishLocal({
				type: "pages.deleted",
				pageObjectNumbers,
				...payload.result
			});
			return payload.result;
		});
	}
	flatten(pageObjectNumbers, usage = "display") {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.pages.modify");
			this.guard.assertCapability("doc.annotate.modify");
		} catch (error) {
			return AbortablePromise.rejectReason(error);
		}
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "pages.flatten",
			jobId,
			docId,
			pageObjectNumbers,
			usage
		}) }, { priority: Priority.HIGH });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "pages.flatten") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			if (payload.result.meta !== null) this.publisher.publishLocal({
				type: "pages.flattened",
				...payload.result
			});
			return payload.result;
		});
	}
	insert(bytes, destIndex) {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.pages.assemble");
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const docId = this.docId;
		const buffer = bytes instanceof ArrayBuffer ? bytes : copyToExactBuffer(bytes);
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "pages.insert",
			jobId,
			docId,
			bytes: buffer,
			destIndex
		}, [buffer]) }, { priority: Priority.HIGH });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "pages.insert") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			this.publisher.publishLocal({
				type: "pages.inserted",
				destIndex,
				...payload.result
			});
			return payload.result;
		});
	}
	extract(pageObjectNumbers) {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.download");
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "pages.extract",
			jobId,
			docId,
			pageObjectNumbers
		}) }, { priority: Priority.MEDIUM });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "pages.extract") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			return new Uint8Array(payload.bytes);
		});
	}
};
//#endregion
//#region src/document/LocalDocumentRedactionService.ts
/**
* Document-scoped redaction apply for the local engine (see
* `DocumentRedactionService` for the two-stage model and the layer trust
* boundary). Funnels through the same in-process worker as every other
* mutation, so an apply is sequenced against in-flight annotation writes.
*/
var LocalDocumentRedactionService = class {
	constructor(docId, queue, view, guard, publisher) {
		this.docId = docId;
		this.queue = queue;
		this.view = view;
		this.guard = guard;
		this.publisher = publisher;
	}
	apply(scope) {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.pages.modify");
			this.guard.assertCapability("doc.annotate.modify");
			this.guard.assertCapability("doc.redact");
		} catch (error) {
			return AbortablePromise.rejectReason(error);
		}
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "redaction.apply",
			jobId,
			docId,
			scope
		}) }, { priority: Priority.HIGH });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "redaction.apply") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			if (payload.result.meta !== null) this.publisher.publishLocal({
				type: "redaction.applied",
				...payload.result
			});
			return payload.result;
		});
	}
};
//#endregion
//#region src/document/LocalDocumentSearchService.ts
/**
* Document-scoped search service. `'rects'` mode gates on
* `doc.text.search`; `'full'` (snippets) additionally needs
* `doc.text.copy` — a snippet IS extracted text, so the copy denial must
* hold here too (cloud parity: the server's search route enforces the
* same pair). The worker fans out to `SearchReader`, which serves page
* text from the session's version-keyed corpus cache.
*/
var LocalDocumentSearchService = class {
	constructor(docId, queue, view, guard) {
		this.docId = docId;
		this.queue = queue;
		this.view = view;
		this.guard = guard;
	}
	query(request) {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.text.search");
			if ((request.mode ?? "full") === "full") this.guard.assertCapability("doc.text.copy");
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "search.query",
			jobId,
			docId,
			request
		}) }, { priority: Priority.LOW });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "search.query") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			return payload.slice;
		});
	}
};
//#endregion
//#region src/document/LocalDocumentSecurityService.ts
var LocalDocumentSecurityService = class {
	constructor(initial, docId, queue, view, guard = null) {
		this.docId = docId;
		this.queue = queue;
		this.view = view;
		this.guard = guard;
		_defineProperty(this, "state", void 0);
		this.state = securityStateFromProbe(initial);
	}
	get current() {
		return this.state;
	}
	/**
	* Expanded capability set from the scope + pdf bits supplied at
	* `engine.open()`. Identical algorithm to the cloud SDK's local-
	* fallback path — both call `expandRawScope` from engine-core.
	* Returns an empty array when no ScopeGuard was wired (legacy
	* open path with no scope).
	*/
	get effectiveScope() {
		return this.guard ? this.guard.effectiveScope() : [];
	}
	/**
	* Wildcard-aware authorization check — the same predicate the page/
	* annotation services enforce with (`ScopeGuard.can`/`assertCapability`).
	* Returns `false` on the legacy no-ScopeGuard open path (no scope was
	* supplied, so we can't affirm a grant — UI should hide edit affordances).
	*/
	allows(cap) {
		return this.guard ? this.guard.can(cap) : false;
	}
	/** Identity claims supplied at `engine.open()`, or null when none. */
	get identity() {
		if (!this.guard) return null;
		const id = this.guard.identity();
		return id && Object.keys(id).length > 0 ? id : null;
	}
	/**
	* "Should I prompt for a password?" — single source of truth,
	* computed via the same `passwordPromptFromState` helper the cloud
	* SDK calls. Identical contract across engines.
	*/
	get passwordPrompt() {
		return passwordPromptFromState(this.state);
	}
	unlock(input) {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "document.checkPasswordPermissions",
			jobId,
			docId: this.docId,
			password: input.password,
			mode: input.mode ?? "any"
		}) }, { priority: Priority.HIGH });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "document.checkPasswordPermissions") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			this.state = securityStateFromProbe(payload.security);
			return { security: this.state };
		});
	}
};
//#endregion
//#region src/document/LocalMetadataService.ts
var LocalMetadataService = class {
	constructor(docId, queue, view, guard, publisher) {
		this.docId = docId;
		this.queue = queue;
		this.view = view;
		this.guard = guard;
		this.publisher = publisher;
	}
	read() {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.open");
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "metadata.read",
			jobId,
			docId
		}) }, { priority: Priority.MEDIUM });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "metadata.read") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			return payload.metadata;
		});
	}
	update(patch) {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.metadata.modify");
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const docId = this.docId;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "metadata.update",
			jobId,
			docId,
			patch
		}) }, { priority: Priority.HIGH });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "metadata.update") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			this.publisher.publishLocal({
				type: "metadata.updated",
				...payload.result
			});
			return payload.result;
		});
	}
};
//#endregion
//#region src/render/renderPolicyGuard.ts
/**
* Local mirror of the server's render-lattice enforcement.
*
* A `localEngine({ renderPolicy })` deployment opts the local engine into
* the SAME policy discipline the cloud advertises over `/v1/access`:
*
*   - BUDGET (always, lattice or not enforced): `maxRenderPixels` rides
*     into every worker render as `maxOutputPixels`, so the rasterizer
*     rejects BEFORE allocating — a page-sized stamp at scale 40 fails
*     fast instead of OOM-killing the tab. A caller-supplied budget can
*     only tighten the deployment's, never loosen it (min wins).
*   - ENFORCEMENT (`enforced: true` only): off-lattice requests are
*     rejected with the same InvalidArg + `renderPolicy` details the
*     enforcing server returns, so conformance bugs surface in dev
*     against a local engine instead of in prod against the CDN plane.
*     Scoping mirrors the server: full-page renders must use a ladder
*     width (rect targets are the future tile policy's jurisdiction and
*     stay exempt); appearance scales must sit on the appearance lattice
*     when the policy declares one.
*
* The DEFAULT local policy remains `continuous` — every check here is the
* identity unless the embedder configured a lattice.
*/
/** Fold the deployment's pixel budget into page-render worker options. */
function withRenderBudget(policy, options) {
	return foldBudget(policy, options);
}
/** Fold the deployment's pixel budget into appearance worker options. */
function withAppearanceBudget(policy, options) {
	return foldBudget(policy, options);
}
function foldBudget(policy, options) {
	if (policy.kind !== "lattice" || policy.maxRenderPixels === void 0) return options;
	const budget = options?.maxOutputPixels !== void 0 ? Math.min(options.maxOutputPixels, policy.maxRenderPixels) : policy.maxRenderPixels;
	return {
		...options ?? {},
		maxOutputPixels: budget
	};
}
/**
* Enforced-lattice check for a FULL-PAGE render. Throws the same
* InvalidArg-with-policy the enforcing server's 400 carries. Rect
* targets pass untouched because they belong to the tile policy.
*/
function assertFullPageOnLattice(policy, options) {
	if (policy.kind !== "lattice" || !policy.enforced) return;
	if (!(options?.target === void 0 || options.target.kind === "page")) return;
	const viewport = options?.viewport;
	const width = viewport?.kind === "width" ? viewport.width : void 0;
	if (width !== void 0 && policy.fullPage.widths.includes(width)) return;
	rejectOffLattice(policy, "use snapFullPageViewport(policy, viewport, { pageWidth })");
}
/**
* Enforced-lattice check for an appearance batch. Only applies when the
* policy declares an appearance lattice — a lattice without one leaves
* appearance scales unbounded (the budget still applies).
*/
function assertAppearanceOnLattice(policy, options) {
	if (policy.kind !== "lattice" || !policy.enforced || policy.appearances === void 0) return;
	const scale = options?.scale ?? 1;
	if (policy.appearances.scales.includes(scale)) return;
	rejectOffLattice(policy, "use snapAppearanceScale(policy, scale)");
}
function rejectOffLattice(policy, hint) {
	throw new EngineError(EngineErrorCode.InvalidArg, `render request is off the deployment lattice (see renderPolicy; ${hint})`, { details: { renderPolicy: policy } });
}
//#endregion
//#region src/document/LocalPageAnnotationsService.ts
/**
* Page-scoped annotation service. `list()` is the slow path (acquires a
* pagePtr server-side). Mutation methods are wired to the in-process
* worker; the worker host runs `AnnotationMutator` synchronously
* inside the same PDFium runtime instance the read path uses, so create
* sees its own writes immediately.
*
* Every mutation publishes its result to the document's event stream
* AFTER the worker confirms — ground truth, never optimistic.
*/
var LocalPageAnnotationsService = class {
	constructor(docId, pageObjectNumber, queue, view, encoder, guard, publisher, policy = CONTINUOUS_RENDER_POLICY) {
		this.docId = docId;
		this.pageObjectNumber = pageObjectNumber;
		this.queue = queue;
		this.view = view;
		this.encoder = encoder;
		this.guard = guard;
		this.publisher = publisher;
		this.policy = policy;
	}
	list() {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.annotate.read");
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const docId = this.docId;
		const pon = this.pageObjectNumber;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "annotations.listFullPage",
			jobId,
			docId,
			pageObjectNumber: pon
		}) }, { priority: Priority.MEDIUM });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "annotations.listFullPage") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			return payload.snapshot;
		});
	}
	downloadFile(ref) {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.download");
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const docId = this.docId;
		const pon = this.pageObjectNumber;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "annotations.readFile",
			jobId,
			docId,
			pageObjectNumber: pon,
			ref
		}) }, { priority: Priority.MEDIUM });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "annotations.readFile") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			const { content } = payload;
			if (content.bytes === void 0) throw new EngineError(EngineErrorCode.WireFormat, "annotations.readFile returned no bytes (path mode is server-only)");
			return {
				bytes: new Uint8Array(content.bytes),
				name: content.name,
				...content.mimeType !== void 0 ? { mimeType: content.mimeType } : {}
			};
		});
	}
	renderAppearances(options) {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.annotate.read");
			assertAppearanceOnLattice(this.policy, options);
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const effectiveOptions = withAppearanceBudget(this.policy, options);
		const docId = this.docId;
		const pon = this.pageObjectNumber;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "annotations.renderAppearances",
			jobId,
			docId,
			pageObjectNumber: pon,
			...effectiveOptions ? { options: effectiveOptions } : {}
		}) }, { priority: Priority.MEDIUM });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "annotations.renderAppearances") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			return payload.result;
		});
	}
	renderAppearanceImages(options = {}) {
		return AbortablePromise.run(async (signal) => {
			const raw = this.renderAppearances(options);
			const onAbort = () => raw.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const result = await raw;
			if (signal.aborted) throw new EngineError(EngineErrorCode.Aborted, "annotation appearance render aborted");
			const appearances = [];
			for (const appearance of result.appearances) {
				if (signal.aborted) throw new EngineError(EngineErrorCode.Aborted, "annotation appearance render aborted");
				const encoded = await this.encoder.encode(appearance.raster, options, signal);
				if (encoded.source.kind !== "bytes") throw new EngineError(EngineErrorCode.WireFormat, "local appearance image handle expected a byte source");
				const bytes = encoded.source.bytes;
				const image = createPageImageHandle(encoded, { async blob() {
					return new Blob([copyToExactArrayBuffer$1(bytes)], { type: encoded.contentType });
				} });
				appearances.push({
					ref: appearance.ref,
					mode: appearance.mode,
					rect: appearance.rect,
					image
				});
			}
			return {
				pageState: result.pageState,
				appearances
			};
		});
	}
	create(draft) {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			const target = this.guard.targetForSelfCreate();
			this.guard.assertCollab("create", target);
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const actor = this.guard.actorForCreate();
		const docId = this.docId;
		const pon = this.pageObjectNumber;
		return AbortablePromise.run(async (signal) => {
			const { wire, resources } = await normalizeAnnotationDraft(draft);
			const resourceBuffers = Object.values(resources).map((r) => r.bytes);
			const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
				kind: "annotations.create",
				jobId,
				docId,
				pageObjectNumber: pon,
				draft: wire,
				...resourceBuffers.length > 0 ? { resources } : {},
				...actor ? { actor } : {}
			}, resourceBuffers) }, { priority: Priority.HIGH });
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "annotations.create") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			this.publisher.publishLocal({
				type: "annotation.created",
				pageObjectNumber: pon,
				...payload.result
			});
			return payload.result;
		});
	}
	update(ref, patch) {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		return AbortablePromise.run(async (signal) => {
			const target = await this.collabTargetForRef(ref, signal);
			this.guard.assertCollab("update", target);
			const patchGroupId = patch.groupId;
			if (typeof patchGroupId === "string" && patchGroupId !== target.groupId) this.guard.assertSetGroup(patchGroupId);
			const actor = this.guard.actorForUpdate(target.groupId, patchGroupId);
			const docId = this.docId;
			const { wire, resources } = await normalizeAnnotationPatch(patch);
			const resourceBuffers = Object.values(resources).map((r) => r.bytes);
			const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
				kind: "annotations.update",
				jobId,
				docId,
				ref,
				patch: wire,
				...resourceBuffers.length > 0 ? { resources } : {},
				...actor ? { actor } : {}
			}, resourceBuffers) }, { priority: Priority.HIGH });
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "annotations.update") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			this.publisher.publishLocal({
				type: "annotation.updated",
				pageObjectNumber: this.pageObjectNumber,
				...payload.result
			});
			return payload.result;
		});
	}
	delete(ref) {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		return AbortablePromise.run(async (signal) => {
			const target = await this.collabTargetForRef(ref, signal);
			this.guard.assertCollab("delete", target);
			const docId = this.docId;
			const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
				kind: "annotations.delete",
				jobId,
				docId,
				ref
			}) }, { priority: Priority.HIGH });
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "annotations.delete") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			this.publisher.publishLocal({
				type: "annotation.deleted",
				pageObjectNumber: this.pageObjectNumber,
				...payload.result
			});
			return payload.result;
		});
	}
	move(refs, toIndex) {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.annotate.modify");
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const docId = this.docId;
		const pon = this.pageObjectNumber;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "annotations.move",
			jobId,
			docId,
			pageObjectNumber: pon,
			refs,
			toIndex
		}) }, { priority: Priority.HIGH });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "annotations.move") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			this.publisher.publishLocal({
				type: "annotation.moved",
				pageObjectNumber: pon,
				...payload.result
			});
			return payload.result;
		});
	}
	/**
	* Resolve the collab subject (userId / groupId) of the target row
	* an UPDATE or DELETE is about to act on. Mirrors the cloud's
	* `LayerService.getAnnotationCollabTarget` — page-fetch + filter
	* over the existing listFullPage worker job. Returns `{}` when the
	* row can't be located; the collab resolver then denies
	* `:self`/`:group=X` filters and the mutator's own InvalidReference
	* surfaces the real error.
	*/
	async collabTargetForRef(ref, signal) {
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "annotations.listFullPage",
			jobId,
			docId: this.docId,
			pageObjectNumber: ref.pageObjectNumber
		}) }, { priority: Priority.MEDIUM });
		const onAbort = () => submission.abort(signal.reason);
		if (signal.aborted) onAbort();
		else signal.addEventListener("abort", onAbort, { once: true });
		const payload = await submission;
		if (payload.tag !== "annotations.listFullPage") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag while resolving collab target: ${payload.tag}`);
		const match = payload.snapshot.annotations.find((a) => {
			switch (ref.kind) {
				case "objectNumber": return a.ref.kind === "objectNumber" && a.ref.annotObjectNumber === ref.annotObjectNumber;
				case "nm": return a.nm === ref.nm;
				case "index": return a.index === ref.index;
			}
		});
		if (!match) return {};
		return {
			...match.userId !== void 0 ? { userId: match.userId } : {},
			...match.groupId !== void 0 ? { groupId: match.groupId } : {}
		};
	}
};
function copyToExactArrayBuffer$1(bytes) {
	const body = new ArrayBuffer(bytes.byteLength);
	new Uint8Array(body).set(bytes);
	return body;
}
//#endregion
//#region src/document/LocalPageGeometryService.ts
var LocalPageGeometryService = class {
	constructor(docId, pageObjectNumber, queue, view, guard) {
		this.docId = docId;
		this.pageObjectNumber = pageObjectNumber;
		this.queue = queue;
		this.view = view;
		this.guard = guard;
	}
	read() {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.text.select");
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const docId = this.docId;
		const pon = this.pageObjectNumber;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "pages.geometry",
			jobId,
			docId,
			pageObjectNumber: pon
		}) }, { priority: Priority.MEDIUM });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "pages.geometry") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			return payload.snapshot;
		});
	}
};
//#endregion
//#region src/document/LocalPageRenderService.ts
var LocalPageRenderService = class {
	constructor(docId, pageObjectNumber, queue, view, encoder, guard, policy = CONTINUOUS_RENDER_POLICY) {
		this.docId = docId;
		this.pageObjectNumber = pageObjectNumber;
		this.queue = queue;
		this.view = view;
		this.encoder = encoder;
		this.guard = guard;
		this.policy = policy;
	}
	raw(options) {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.render");
			assertFullPageOnLattice(this.policy, options);
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const effectiveOptions = withRenderBudget(this.policy, options);
		const docId = this.docId;
		const pon = this.pageObjectNumber;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "pages.render",
			jobId,
			docId,
			pageObjectNumber: pon,
			options: effectiveOptions
		}) }, { priority: Priority.HIGH });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "pages.render") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			return payload.raster;
		});
	}
	image(options = {}) {
		return AbortablePromise.run(async (signal) => {
			const raw = this.raw(options);
			const onAbort = () => raw.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const raster = await raw;
			if (signal.aborted) throw new EngineError(EngineErrorCode.Aborted, "page image render aborted");
			const result = await this.encoder.encode(raster, options, signal);
			if (result.source.kind !== "bytes") throw new EngineError(EngineErrorCode.WireFormat, "local page image handle expected a byte source");
			const bytes = result.source.bytes;
			return createPageImageHandle(result, { async blob() {
				return new Blob([copyToExactArrayBuffer(bytes)], { type: result.contentType });
			} });
		});
	}
};
function copyToExactArrayBuffer(bytes) {
	const body = new ArrayBuffer(bytes.byteLength);
	new Uint8Array(body).set(bytes);
	return body;
}
//#endregion
//#region src/document/LocalPageTextService.ts
/**
* Page-scoped text service. `read()` enqueues a `pages.text` worker
* request — the host runs `PageTextReader` against the same PDFium
* session the annotations service uses, so a `read()` issued
* immediately after a mutation observes the post-mutation page.
*
* Mirrors `LocalPageAnnotationsService.list()` so the worker plumbing
* is symmetric across every per-page read.
*/
var LocalPageTextService = class {
	constructor(docId, pageObjectNumber, queue, view, guard) {
		this.docId = docId;
		this.pageObjectNumber = pageObjectNumber;
		this.queue = queue;
		this.view = view;
		this.guard = guard;
	}
	read() {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability("doc.text.copy");
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const docId = this.docId;
		const pon = this.pageObjectNumber;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "pages.text",
			jobId,
			docId,
			pageObjectNumber: pon
		}) }, { priority: Priority.MEDIUM });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "pages.text") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			return payload.snapshot;
		});
	}
};
//#endregion
//#region src/document/LocalPieceInfoService.ts
/**
* `/PieceInfo` access for the local engine. ONE class serves both levels —
* `pageObjectNumber` undefined targets the document catalog, set targets a
* page — mirroring the wire protocol's single job family.
*
* Authorization: reads ride `doc.open` (session-level read, like
* `pages.list`); writes ride `doc.metadata.modify` — piece data is
* metadata-shaped private state, so it reuses the metadata write gate
* rather than minting a new scope. Revisit if a cloud consumer needs
* finer granularity.
*/
var LocalPieceInfoService = class {
	constructor(docId, queue, view, guard, pageObjectNumber) {
		this.docId = docId;
		this.queue = queue;
		this.view = view;
		this.guard = guard;
		this.pageObjectNumber = pageObjectNumber;
	}
	read(application) {
		const rejected = this.gate("doc.open");
		if (rejected) return rejected;
		const { docId, pageObjectNumber } = this;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "pieceInfo.read",
			jobId,
			docId,
			pageObjectNumber,
			application
		}) }, { priority: Priority.MEDIUM });
		return AbortablePromise.run(async (signal) => {
			const payload = await this.await(submission, signal);
			if (payload.tag !== "pieceInfo.read") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			return payload.snapshot;
		});
	}
	update(application, patch) {
		const rejected = this.gate("doc.metadata.modify");
		if (rejected) return rejected;
		const { docId, pageObjectNumber } = this;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "pieceInfo.update",
			jobId,
			docId,
			pageObjectNumber,
			application,
			patch
		}) }, { priority: Priority.HIGH });
		return AbortablePromise.run(async (signal) => {
			const payload = await this.await(submission, signal);
			if (payload.tag !== "pieceInfo.update") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
		});
	}
	applications() {
		const rejected = this.gate("doc.open");
		if (rejected) return rejected;
		const { docId, pageObjectNumber } = this;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "pieceInfo.applications",
			jobId,
			docId,
			pageObjectNumber
		}) }, { priority: Priority.MEDIUM });
		return AbortablePromise.run(async (signal) => {
			const payload = await this.await(submission, signal);
			if (payload.tag !== "pieceInfo.applications") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			return payload.applications;
		});
	}
	clear(application) {
		const rejected = this.gate("doc.metadata.modify");
		if (rejected) return rejected;
		const { docId, pageObjectNumber } = this;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "pieceInfo.clear",
			jobId,
			docId,
			pageObjectNumber,
			application
		}) }, { priority: Priority.HIGH });
		return AbortablePromise.run(async (signal) => {
			const payload = await this.await(submission, signal);
			if (payload.tag !== "pieceInfo.clear") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
		});
	}
	/** Shared closed-check + capability gate; null when the call may proceed. */
	gate(capability) {
		if (this.view.isClosed()) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.docId}`));
		try {
			this.guard.assertCapability(capability);
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		return null;
	}
	/** Wire an outer abort into the queued submission (the house idiom). */
	async await(submission, signal) {
		const onAbort = () => submission.abort(signal.reason);
		if (signal.aborted) onAbort();
		else signal.addEventListener("abort", onAbort, { once: true });
		return await submission;
	}
};
//#endregion
//#region src/document/LocalPageHandle.ts
/**
* Local page handle. `pageIndex` is supplied as advisory metadata when
* the document handle creates the page handle. The per-page services
* each own their queue interaction; the handle is otherwise stateless.
*/
var LocalPageHandle = class {
	constructor(pageObjectNumber, pageIndex, docId, queue, view, imageEncoder, guard, publisher, renderPolicy = CONTINUOUS_RENDER_POLICY) {
		this.pageObjectNumber = pageObjectNumber;
		this.pageIndex = pageIndex;
		_defineProperty(this, "annotations", void 0);
		_defineProperty(this, "text", void 0);
		_defineProperty(this, "geometry", void 0);
		_defineProperty(this, "render", void 0);
		_defineProperty(this, "pieceInfo", void 0);
		this.annotations = new LocalPageAnnotationsService(docId, pageObjectNumber, queue, view, imageEncoder, guard, publisher, renderPolicy);
		this.text = new LocalPageTextService(docId, pageObjectNumber, queue, view, guard);
		this.geometry = new LocalPageGeometryService(docId, pageObjectNumber, queue, view, guard);
		this.render = new LocalPageRenderService(docId, pageObjectNumber, queue, view, imageEncoder, guard, renderPolicy);
		this.pieceInfo = new LocalPieceInfoService(docId, queue, view, guard, pageObjectNumber);
	}
};
//#endregion
//#region src/document/LocalDocumentHandle.ts
var LocalDocumentHandle = class {
	constructor(id, queue, imageEncoder, initialSecurity, guard, sessionId, renderPolicy = CONTINUOUS_RENDER_POLICY) {
		this.id = id;
		this.queue = queue;
		this.imageEncoder = imageEncoder;
		this.guard = guard;
		_defineProperty(this, "capabilities", {
			weakAnnotationEditSessions: "not-needed",
			pageEditSessions: "unsupported"
		});
		_defineProperty(this, "metadata", void 0);
		_defineProperty(this, "pieceInfo", void 0);
		_defineProperty(this, "annotations", void 0);
		_defineProperty(this, "attachments", void 0);
		_defineProperty(this, "actions", void 0);
		_defineProperty(this, "forms", void 0);
		_defineProperty(this, "search", void 0);
		_defineProperty(this, "pages", void 0);
		_defineProperty(this, "redaction", void 0);
		_defineProperty(this, "security", void 0);
		_defineProperty(this, "render", void 0);
		_defineProperty(this, "events", void 0);
		_defineProperty(this, "publisher", void 0);
		_defineProperty(this, "renderPolicy", void 0);
		_defineProperty(this, "closed", false);
		const view = { isClosed: () => this.closed };
		this.renderPolicy = renderPolicy;
		this.render = { policy: () => Promise.resolve(this.renderPolicy) };
		const hub = new EventHub();
		this.events = hub;
		this.publisher = new SessionEventPublisher(hub, sessionId);
		this.security = new LocalDocumentSecurityService(initialSecurity, id, queue, view, guard);
		this.metadata = new LocalMetadataService(id, queue, view, guard, this.publisher);
		this.pieceInfo = new LocalPieceInfoService(id, queue, view, guard);
		this.annotations = new LocalDocumentAnnotationsService(id, queue, view, guard);
		this.attachments = new LocalDocumentAttachmentsService(id, queue, view, guard, this.publisher);
		this.actions = new LocalDocumentActionsService(id, queue, view, guard);
		this.forms = new LocalDocumentFormsService(id, queue, view, guard, this.publisher);
		this.search = new LocalDocumentSearchService(id, queue, view, guard);
		this.pages = new LocalDocumentPagesService(id, queue, view, guard, this.publisher);
		this.redaction = new LocalDocumentRedactionService(id, queue, view, guard, this.publisher);
	}
	/**
	* Returns a `PageHandle` keyed on the page's PDF indirect object
	* number. We don't validate the page exists synchronously - the worker
	* does that on the next call. This matches the cloud engine, which
	* cannot validate without a round-trip either.
	*
	* `pageIndex` is advisory metadata, reported as `-1`. Display order is
	* geometry, not liveness: clients read it from `pages.list()` (each
	* `PageLayout.index`), joined to this handle by `pageObjectNumber`.
	*/
	page(pageObjectNumber) {
		return new LocalPageHandle(pageObjectNumber, -1, this.id, this.queue, { isClosed: () => this.closed }, this.imageEncoder, this.guard, this.publisher, this.renderPolicy);
	}
	download(opts = {}) {
		if (this.closed) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.id}`));
		try {
			this.guard.assertCapability("doc.download");
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const docId = this.id;
		const mode = opts.mode ?? DEFAULT_PDF_SAVE_MODE;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "document.saveBuffer",
			jobId,
			docId,
			mode
		}) }, { priority: Priority.HIGH });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "document.saveBuffer") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			return new Uint8Array(payload.bytes);
		});
	}
	/** Export just this document's layer as a re-openable artifact. Rejects when the
	*  document was opened without a layer (the worker rejects a base-only session). */
	downloadLayer() {
		if (this.closed) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.DocNotOpen, `document not open: ${this.id}`));
		try {
			this.guard.assertCapability("doc.download");
		} catch (err) {
			return AbortablePromise.rejectReason(err);
		}
		const docId = this.id;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "document.saveLayerBuffer",
			jobId,
			docId
		}) }, { priority: Priority.HIGH });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "document.saveLayerBuffer") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			return new Uint8Array(payload.bytes);
		});
	}
	close() {
		if (this.closed) return AbortablePromise.resolveValue(void 0);
		this.closed = true;
		const docId = this.id;
		const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "close",
			jobId,
			docId
		}) }, { priority: Priority.HIGH });
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			await submission;
		});
	}
};
//#endregion
//#region src/fonts/LocalFontService.ts
/**
* Main-thread implementation of {@link FontService} for the local engine.
*
* It is the authoritative store of registered fonts; the worker holds only the
* derived `fontKey → native FontId` map. Registration broadcasts to the worker
* over the same {@link WorkerQueue} as every other operation, so a font is
* always registered before any annotation create/update that references it can
* run (the queue serializes through one host).
*
* Keeping the bytes here also lets us {@link replay} the full registry, in
* order, onto a freshly spawned worker — which is what keeps `fontKey`s stable
* across a worker restart (and would extend to a worker pool): each worker
* re-derives the same ids from the same registration order.
*/
var LocalFontService = class {
	constructor(queue) {
		this.queue = queue;
		_defineProperty(this, "fonts", /* @__PURE__ */ new Map());
		_defineProperty(this, "fallbacks", []);
	}
	register(spec) {
		const bytes = toArrayBuffer$1(spec.data);
		const key = spec.key;
		const existing = this.fonts.get(key);
		if (existing) return AbortablePromise.resolveValue(existing.handle);
		const familyName = spec.familyName ?? "";
		const weight = spec.weight ?? 0;
		const italic = spec.italic === void 0 ? -1 : spec.italic ? 1 : 0;
		const handle = {
			key,
			familyName,
			weight,
			italic: spec.italic ?? false
		};
		return AbortablePromise.run(async (signal) => {
			const replayCopy = bytes.slice(0);
			const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
				kind: "fonts.register",
				jobId,
				fontKey: key,
				familyName,
				weight,
				italic,
				data: bytes
			}, [bytes]) }, { priority: Priority.HIGH });
			forwardAbort(signal, submission);
			await submission;
			this.fonts.set(key, {
				handle,
				italic,
				weight,
				familyName,
				bytes: replayCopy
			});
			return handle;
		});
	}
	/**
	* Record a font the boot pipeline already registered on the host by raw
	* transport send (boot-config fonts bypass the queue: they must reach the
	* worker before any buffered user job flushes, and going through the queue
	* during boot would deadlock on the concurrency slot a buffered `open()`
	* already holds). Keeps `list()`, idempotent `register()`, and `replay()`
	* consistent with what the worker has.
	*
	* @internal — not part of the public FontService contract.
	*/
	seedRegistered(spec, opts = {}) {
		const key = spec.key;
		let entry = this.fonts.get(key);
		if (!entry) {
			entry = {
				handle: {
					key,
					familyName: spec.familyName ?? "",
					weight: spec.weight ?? 0,
					italic: spec.italic ?? false
				},
				italic: spec.italic === void 0 ? -1 : spec.italic ? 1 : 0,
				weight: spec.weight ?? 0,
				familyName: spec.familyName ?? "",
				bytes: toArrayBuffer$1(spec.data)
			};
			this.fonts.set(key, entry);
		}
		if (opts.fallback && !this.fallbacks.includes(key)) this.fallbacks.push(key);
		return entry.handle;
	}
	registerAll(specs) {
		return AbortablePromise.run(async (signal) => {
			const handles = [];
			for (const spec of specs) {
				const submission = this.register(spec);
				forwardAbort(signal, submission);
				handles.push(await submission);
			}
			return handles;
		});
	}
	addFallback(font) {
		const key = typeof font === "string" ? font : font.key;
		if (!this.fonts.has(key)) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.InvalidArg, `font not registered: ${key}`));
		return AbortablePromise.run(async (signal) => {
			const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
				kind: "fonts.addFallback",
				jobId,
				fontKey: key
			}) });
			forwardAbort(signal, submission);
			await submission;
			if (!this.fallbacks.includes(key)) this.fallbacks.push(key);
		});
	}
	clearFallbacks() {
		return AbortablePromise.run(async (signal) => {
			const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
				kind: "fonts.clearFallbacks",
				jobId
			}) });
			forwardAbort(signal, submission);
			await submission;
			this.fallbacks.length = 0;
		});
	}
	clear() {
		return AbortablePromise.run(async (signal) => {
			const submission = this.queue.enqueue({ buildPack: (jobId) => wirePack({
				kind: "fonts.clear",
				jobId
			}) });
			forwardAbort(signal, submission);
			await submission;
			this.fonts.clear();
			this.fallbacks.length = 0;
		});
	}
	list() {
		return [...this.fonts.values()].map((f) => f.handle);
	}
	/**
	* Re-issue the entire registry to a freshly (re)spawned worker, in original
	* order, so `fontKey → id` rebinds deterministically. Not yet called by the
	* single-worker engine, but the contract a worker pool / crash-recovery path
	* would use. Sends fresh copies so the retained bytes are never neutered.
	*/
	async replay() {
		for (const [key, font] of this.fonts) {
			const copy = font.bytes.slice(0);
			await this.queue.enqueue({ buildPack: (jobId) => wirePack({
				kind: "fonts.register",
				jobId,
				fontKey: key,
				familyName: font.familyName,
				weight: font.weight,
				italic: font.italic,
				data: copy
			}, [copy]) });
		}
		for (const key of this.fallbacks) await this.queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "fonts.addFallback",
			jobId,
			fontKey: key
		}) });
	}
};
function forwardAbort(signal, submission) {
	const onAbort = () => submission.abort(signal.reason);
	if (signal.aborted) onAbort();
	else signal.addEventListener("abort", onAbort, { once: true });
}
function toArrayBuffer$1(input) {
	if (input instanceof ArrayBuffer) return input.slice(0);
	const copy = new ArrayBuffer(input.byteLength);
	new Uint8Array(copy).set(input);
	return copy;
}
//#endregion
//#region src/wasm-source.ts
/**
* Where does `embedpdf.wasm` come from?
*
* The wasm binary is the ONE runtime-fetched asset of the local engine.
* Everything else (the worker code) travels through the module graph, so it
* never needs bundler asset handling — but the 6 MB binary is fetched at
* runtime from a plain URL, resolved here on the MAIN THREAD (the worker
* never guesses) in a fixed precedence order:
*
*   1. `wasmBinary`  — caller-supplied bytes, zero network (air-gapped).
*   2. `wasmUrl`     — exact URL.
*   3. `assetsUrl`   — base directory; `embedpdf.wasm` is appended.
*   4. the default   — depends on how the worker itself is delivered:
*      - inline blob worker (the zero-config path): SIBLING-FIRST. The
*        bundler-resolved URL from `@embedpdf/engine-runtime-wasm32/wasm-url`
*        (the wasm ships inside the consumer's own build), with the
*        version-pinned jsDelivr URL as a fetch-failure-only fallback. A blob
*        worker has no meaningful location, so both URLs are resolved here on
*        the main thread and must be absolute.
*      - a real worker URL / caller-built worker: nothing is sent, and the
*        Emscripten glue resolves `embedpdf.wasm` as a SIBLING of the worker
*        script (`import.meta.url`). Copying `embedpdf-worker.js` and
*        `embedpdf.wasm` into one directory is a complete self-host setup, and
*        bundler-emitted workers (Vite `?worker`) keep their bundler-managed
*        asset next to the worker chunk.
*/
const DEFAULT_WASM_URL = `https://cdn.jsdelivr.net/npm/@embedpdf/engine-runtime-wasm32@3.0.0-next.0/lib/embedpdf.wasm`;
/**
* Resolve the caller's wasm options into what the worker init carries.
* Explicit sources only — the inline blob worker's bundler-resolved default
* lives in {@link resolveInlineWasmSource}; every other worker delivery
* self-resolves the wasm as a sibling of the worker script when no explicit
* source is given (hence the empty result).
*/
function resolveWasmSource(options) {
	if (options.wasmBinary !== void 0) return { wasmBinary: toStandaloneBuffer(options.wasmBinary) };
	if (options.wasmUrl !== void 0) return { wasmUrl: toAbsoluteUrl(options.wasmUrl) };
	if (options.assetsUrl !== void 0) return { wasmUrl: toAbsoluteUrl(`${options.assetsUrl.endsWith("/") ? options.assetsUrl : `${options.assetsUrl}/`}embedpdf.wasm`) };
	return {};
}
/**
* Resolve the wasm source for the inline blob worker, which cannot
* self-resolve (a blob URL has no meaningful location). Explicit options win;
* otherwise the default is SIBLING-FIRST:
*
*   1. `@embedpdf/engine-runtime-wasm32/wasm-url` — a static
*      `new URL('./lib/embedpdf.wasm', import.meta.url)` that bundlers resolve
*      at build time, shipping the wasm inside the consumer's own build.
*   2. the version-pinned jsDelivr URL as `fallbackWasmUrl` — used by the
*      worker only when fetching (1) fails (a toolchain that left the URL
*      pointing somewhere the wasm is not).
*
* The import CANNOT fail under a bundler — an unresolvable specifier is a
* build-time error there (desired: it fails loudly at build). The catch only
* covers native-ESM/no-bundler runtimes without the package installed, where
* the pinned CDN URL becomes the primary source.
*/
async function resolveInlineWasmSource(options) {
	const explicit = resolveWasmSource(options);
	if (explicit.wasmUrl !== void 0 || explicit.wasmBinary !== void 0) return explicit;
	try {
		const sibling = (await import("@embedpdf/engine-runtime-wasm32/wasm-url")).default;
		if (typeof sibling === "string" && sibling.length > 0) return {
			wasmUrl: toAbsoluteUrl(sibling),
			fallbackWasmUrl: DEFAULT_WASM_URL
		};
	} catch {}
	return { wasmUrl: DEFAULT_WASM_URL };
}
/**
* Resolve against the page NOW: a relative URL like `/assets/embedpdf.wasm`
* cannot be resolved inside a `blob:` worker (blob URLs are not hierarchical),
* so the absolute form must cross the postMessage boundary.
*/
function toAbsoluteUrl(url) {
	if (typeof document !== "undefined") return new URL(url, document.baseURI).href;
	if (typeof location !== "undefined") return new URL(url, location.href).href;
	return url;
}
/**
* Copy to a standalone ArrayBuffer: the init message TRANSFERS the buffer to
* the worker, and neutering the caller's copy would break a second engine
* created from the same options (or a larger buffer the caller still owns).
*/
function toStandaloneBuffer(input) {
	if (input instanceof Uint8Array) {
		const copy = new ArrayBuffer(input.byteLength);
		new Uint8Array(copy).set(input);
		return copy;
	}
	return input.slice(0);
}
//#endregion
//#region src/render/bmp.ts
function encodeBmp(rgba, width, height) {
	const pixels = width * height * 4;
	const headerLength = 66;
	const out = new Uint8Array(headerLength + pixels);
	const view = new DataView(out.buffer);
	out[0] = 66;
	out[1] = 77;
	view.setUint32(2, headerLength + pixels, true);
	view.setUint32(10, headerLength, true);
	view.setUint32(14, 40, true);
	view.setInt32(18, width, true);
	view.setInt32(22, -height, true);
	view.setUint16(26, 1, true);
	view.setUint16(28, 32, true);
	view.setUint32(30, 3, true);
	view.setUint32(34, pixels, true);
	view.setUint32(54, 255, true);
	view.setUint32(58, 65280, true);
	view.setUint32(62, 16711680, true);
	out.set(rgba, headerLength);
	return out;
}
//#endregion
//#region src/render/encoder-worker-source.ts
/**
* The image-encoder worker, as source text — ONE definition serving both
* deliveries so they can never drift:
*
*   - at runtime, `BrowserImageEncoder` spawns it from a blob URL (default);
*   - at build time, `scripts/build-workers.mjs` writes it verbatim to
*     `workers/encoder-worker.js`, the same-origin static file strict-CSP
*     users point `encoderWorker` at.
*
* Deliberately dependency-free classic-worker code: it must run identically
* as a blob and as a plain file.
*/
const encoderWorkerSource = `
self.onmessage = async (event) => {
  const { id, raster, format, quality } = event.data;
  try {
    if (typeof OffscreenCanvas === 'undefined') {
      throw new Error('OffscreenCanvas is not available in this worker');
    }
    const bytes = raster.data;
    const image = new ImageData(new Uint8ClampedArray(bytes), raster.width, raster.height);
    const canvas = new OffscreenCanvas(raster.width, raster.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas context is unavailable');
    ctx.putImageData(image, 0, 0);
    const blob = await canvas.convertToBlob({ type: 'image/' + format, quality });
    const encoded = await blob.arrayBuffer();
    self.postMessage({ id, ok: true, bytes: encoded }, [encoded]);
  } catch (err) {
    self.postMessage({ id, ok: false, error: err && err.message ? err.message : String(err) });
  }
};
`;
//#endregion
//#region src/render/BrowserImageEncoder.ts
var BrowserImageEncoder = class {
	constructor(opts = {}) {
		this.opts = opts;
		_defineProperty(this, "pending", /* @__PURE__ */ new Map());
		_defineProperty(this, "workers", []);
		_defineProperty(this, "workerUrl", null);
		_defineProperty(this, "nextWorker", 0);
		_defineProperty(this, "nextId", 1);
		_defineProperty(this, "disabledWorkerPath", false);
		_defineProperty(this, "poolVerified", false);
	}
	async encode(raster, options, signal) {
		const format = options.format ?? "png";
		if (format === "bmp") return {
			width: raster.width,
			height: raster.height,
			format,
			contentType: "image/bmp",
			source: {
				kind: "bytes",
				bytes: encodeBmp(new Uint8Array(raster.data), raster.width, raster.height)
			}
		};
		const bytes = await this.encodePngOrWebp(raster, format, options.quality, signal);
		return {
			width: raster.width,
			height: raster.height,
			format,
			contentType: contentType(format),
			source: {
				kind: "bytes",
				bytes
			}
		};
	}
	destroy() {
		for (const worker of this.workers) worker.terminate();
		this.workers = [];
		if (this.workerUrl) URL.revokeObjectURL(this.workerUrl);
		this.workerUrl = null;
		for (const task of this.pending.values()) task.reject(new EngineError(EngineErrorCode.Aborted, "image encoder destroyed"));
		this.pending.clear();
	}
	async encodePngOrWebp(raster, format, quality, signal) {
		if (!this.disabledWorkerPath && this.canUseWorkerPath()) try {
			return await this.encodeInWorker(raster, format, quality, signal);
		} catch (error) {
			if (signal.aborted) throw error;
			if (this.poolVerified) {
				this.disabledWorkerPath = true;
				this.destroy();
				throw error;
			}
			this.disabledWorkerPath = true;
			this.destroy();
			warnWorkerFallback(error);
			return await encodeOnMainThread(raster, format, quality, signal);
		}
		return await encodeOnMainThread(raster, format, quality, signal);
	}
	canUseWorkerPath() {
		const source = this.opts.worker ?? "inline";
		if (source === false) return false;
		if (typeof Worker === "undefined" || typeof OffscreenCanvas === "undefined") return false;
		if (source === "inline") return typeof Blob !== "undefined" && typeof URL !== "undefined" && typeof URL.createObjectURL === "function";
		return true;
	}
	encodeInWorker(raster, format, quality, signal) {
		this.ensureWorkers();
		const worker = this.workers[this.nextWorker++ % this.workers.length];
		const id = `img-${this.nextId++}`;
		return new Promise((resolve, reject) => {
			const cleanup = () => {
				this.pending.delete(id);
				signal.removeEventListener("abort", onAbort);
			};
			const onAbort = () => {
				cleanup();
				reject(new EngineError(EngineErrorCode.Aborted, "image encoding aborted"));
			};
			this.pending.set(id, {
				resolve: (bytes) => {
					this.poolVerified = true;
					cleanup();
					resolve(bytes);
				},
				reject: (error) => {
					cleanup();
					reject(error);
				}
			});
			if (signal.aborted) {
				onAbort();
				return;
			}
			signal.addEventListener("abort", onAbort, { once: true });
			const data = this.poolVerified ? raster.data : raster.data.slice(0);
			worker.postMessage({
				id,
				raster: {
					width: raster.width,
					height: raster.height,
					data
				},
				format,
				quality
			}, [data]);
		});
	}
	ensureWorkers() {
		if (this.workers.length > 0) return;
		const count = Math.max(1, this.opts.workerCount ?? 2);
		for (let i = 0; i < count; i++) {
			const worker = this.createWorker();
			worker.onmessage = (event) => {
				const msg = event.data;
				const task = this.pending.get(msg.id);
				if (!task) return;
				if (msg.ok) task.resolve(new Uint8Array(msg.bytes));
				else task.reject(new EngineError(EngineErrorCode.RuntimeUnavailable, msg.error));
			};
			worker.onerror = (event) => {
				for (const task of this.pending.values()) task.reject(new EngineError(EngineErrorCode.RuntimeUnavailable, event.message));
				this.pending.clear();
			};
			this.workers.push(worker);
		}
	}
	createWorker() {
		const source = this.opts.worker ?? "inline";
		if (typeof source === "function") return source();
		if (typeof source === "string") return new Worker(toAbsoluteUrl(source));
		if (!this.workerUrl) this.workerUrl = URL.createObjectURL(new Blob([encoderWorkerSource], { type: "text/javascript" }));
		return new Worker(this.workerUrl);
	}
};
let warnedWorkerFallback = false;
function warnWorkerFallback(error) {
	if (warnedWorkerFallback) return;
	warnedWorkerFallback = true;
	console.warn("[embedpdf] image encoder workers are unavailable — falling back to main-thread encoding (slower under load). If your Content-Security-Policy blocks blob: workers, self-host @embedpdf/engine's workers/encoder-worker.js and pass `encoderWorker: '/path/encoder-worker.js'` to localEngine(). See https://www.embedpdf.com/docs/self-hosting —", error);
}
async function encodeOnMainThread(raster, format, quality, signal) {
	if (signal.aborted) throw new EngineError(EngineErrorCode.Aborted, "image encoding aborted");
	if (typeof document === "undefined" || typeof ImageData === "undefined") throw new EngineError(EngineErrorCode.RuntimeUnavailable, "Canvas image encoding is unavailable in this environment");
	const canvas = document.createElement("canvas");
	canvas.width = raster.width;
	canvas.height = raster.height;
	const ctx = canvas.getContext("2d");
	if (!ctx) throw new EngineError(EngineErrorCode.RuntimeUnavailable, "2D canvas context is unavailable");
	ctx.putImageData(new ImageData(new Uint8ClampedArray(raster.data), raster.width, raster.height), 0, 0);
	const blob = await new Promise((resolve, reject) => {
		canvas.toBlob((value) => value ? resolve(value) : reject(/* @__PURE__ */ new Error("canvas.toBlob returned null")), contentType(format), quality);
	});
	if (signal.aborted) throw new EngineError(EngineErrorCode.Aborted, "image encoding aborted");
	return new Uint8Array(await blob.arrayBuffer());
}
function contentType(format) {
	return `image/${format}`;
}
//#endregion
//#region src/scope/ScopeGuard.ts
/**
* Per-handle authorization helper. Wraps the shared scope resolver
* (`@embedpdf/engine-core/runtime`) bound to one handle's scope +
* identity + PDF bits, and exposes assertion-style methods that throw
* `PermissionDenied` on deny.
*
* Services constructed by `LocalDocumentHandle` take a `ScopeGuard`
* the same way they already take a `DocClosedView` — a small adapter
* with focused methods, easy to mock in tests.
*
* Cloud parity: every method here corresponds to a check the cloud
* route layer performs in `jwt-plugin.ts` / `routes/annotations.ts`.
* The same scope string produces the same allow/deny on both engines.
*/
var ScopeGuard = class {
	constructor(ctx) {
		this.ctx = ctx;
	}
	/** Identity claims (user_id, group_id, groups, display_name). */
	identity() {
		return this.ctx.identity;
	}
	/** Raw scope array as supplied to `open()`. */
	rawScope() {
		return this.ctx.scope;
	}
	/**
	* Concrete capability set after `pdf.permissions` expansion plus the
	* resolver's implication rules. Useful for surfaces that want to
	* report "what can this handle actually do" (mirrors the cloud's
	* `/access` effectiveScope).
	*/
	effectiveScope() {
		return [...expandRawScope(this.ctx.scope, this.ctx.pdfBits)].sort();
	}
	/**
	* Non-throwing capability check — the same predicate `assertCapability`
	* uses, exposed for UI gating (`DocumentSecurityService.allows`). Honors
	* the `*` wildcard and `pdf.permissions` bits, so a gated control mirrors
	* exactly what `assertCapability` would let through.
	*/
	can(cap) {
		return checkCapability(cap, this.ctx.scope, this.ctx.pdfBits);
	}
	/** Throws `PermissionDenied` if the scope doesn't grant `cap`. */
	assertCapability(cap) {
		if (!this.can(cap)) throw new PermissionDenied(cap, "engine-local");
	}
	/**
	* Throws `PermissionDenied` if the scope grants NONE of `caps`. Used
	* by routes whose underlying endpoint is satisfied by more than one
	* capability (currently unused locally; reserved for future shapes
	* like `/text` which the cloud gates on `doc.text.copy OR doc.text.search`).
	*/
	assertAnyCapability(caps) {
		if (!checkAnyCapability(caps, this.ctx.scope, this.ctx.pdfBits)) throw new PermissionDenied(`one of: ${caps.join(", ")}`, "engine-local");
	}
	/**
	* Throws `PermissionDenied` if the scope doesn't grant the collab
	* action against `target`. POST handlers compute `target` from
	* the caller's identity (effective userId/groupId, possibly with
	* draft overrides); PATCH/DELETE handlers compute it from the
	* existing annotation row.
	*/
	assertCollab(action, target) {
		if (!checkCollab(action, target, this.ctx.scope, this.ctx.identity, this.ctx.pdfBits)) throw new PermissionDenied(`annotations:${action}`, "engine-local");
	}
	/**
	* Throws `PermissionDenied` if the scope doesn't grant set-group
	* authority for `newGroupId`. No-op when `newGroupId` matches the
	* caller's JWT-default group (no reassignment is happening).
	*/
	assertSetGroup(newGroupId) {
		if (newGroupId === void 0) return;
		if (!checkSetGroup(newGroupId, this.ctx.identity.group_id, this.ctx.scope, this.ctx.pdfBits)) throw new PermissionDenied(`annotations:set-group:group=${newGroupId}`, "engine-local");
	}
	/**
	* Build the actor that the worker stamps onto a newly created
	* annotation. Identity is sourced from the handle's open-time
	* identity (parity with the cloud's `doc.annotate.create`
	* capability, which stamps the caller's JWT identity).
	*
	*   userId      → /EMBD_Metadata/UserID,CreatedBy,UpdatedBy
	*   groupId     → /EMBD_Metadata/GroupID
	*   displayName → /T (the standard PDF "author" display field)
	*
	* Returns `undefined` when the handle has no identity fields at all
	* (anonymous local handle) — the worker still writes /M but skips
	* both /T and /EMBD_Metadata.
	*/
	actorForCreate() {
		const id = this.ctx.identity;
		const actor = {
			...id.user_id !== void 0 ? { userId: id.user_id } : {},
			...id.group_id !== void 0 ? { groupId: id.group_id } : {},
			...id.display_name !== void 0 ? { displayName: id.display_name } : {}
		};
		return actor.userId || actor.groupId || actor.displayName ? actor : void 0;
	}
	/**
	* Build the CollabTarget for CREATE — the handle's own identity.
	* Fed to `assertCollab('create', target)` so `:self`/`:all` trivially
	* pass and `:group=X` is meaningful (matches when the handle's
	* default group is X).
	*/
	targetForSelfCreate() {
		const id = this.ctx.identity;
		return {
			...id.user_id !== void 0 ? { userId: id.user_id } : {},
			...id.group_id !== void 0 ? { groupId: id.group_id } : {}
		};
	}
	/**
	* Build the actor for an annotation UPDATE.
	*   - userId      → caller's identity (UpdatedBy stamp)
	*   - groupId     → ONLY when the patch reassigns it (differs from current)
	*   - displayName → caller's display_name (for the modification trail;
	*                   the worker does not touch /T on update)
	*
	* No set-group check here — call `assertSetGroup` separately first,
	* before producing the actor.
	*/
	actorForUpdate(currentGroupId, patchGroupId) {
		const id = this.ctx.identity;
		const isReassigning = patchGroupId !== void 0 && patchGroupId !== currentGroupId;
		const actor = {
			...id.user_id !== void 0 ? { userId: id.user_id } : {},
			...id.display_name !== void 0 ? { displayName: id.display_name } : {},
			...isReassigning ? { groupId: patchGroupId } : {}
		};
		return actor.userId || actor.groupId || actor.displayName ? actor : void 0;
	}
};
//#endregion
//#region src/scope/buildHandleScopeContext.ts
/**
* Default scope when the caller omits the `scope` option on
* `LocalEngine.open()`. Wildcard grants every capability and bypasses
* every collab check — the right default for "I just want to open a
* PDF and try things out" prototyping, but not what cloud JWTs look
* like. A one-time console warning fires the first time this default
* is used per process, pointing developers at the realistic-testing
* pattern.
*/
const DEFAULT_LOCAL_SCOPE = ["*"];
/**
* Build the per-handle scope context that drives `ScopeGuard` for
* every operation on this `LocalDocumentHandle`.
*
* Validation performed up front so misconfiguration surfaces at
* `open()` time:
*   - every scope string parses (throws `InvalidScope` on bad strings)
*   - if the scope contains collab filters that need identity
*     (`:self`, `:group=X`), the identity must carry the matching
*     field (throws `MissingIdentity` on a config that would always
*     deny)
*
* Behaviour mirrors what `jwt-plugin.ts` does on the cloud side at
* request time — we just move the same checks to engine-local's
* open-time so they fire once rather than per call.
*/
function buildHandleScopeContext(input) {
	const scope = input.scope ?? DEFAULT_LOCAL_SCOPE;
	if (input.scope === void 0) warnDefaultScopeOnce();
	validateScopeArray(scope);
	const identity = input.identity ?? {};
	assertIdentityForCollabScopes(scope, identity);
	return {
		scope,
		identity,
		pdfBits: decodePdfBits(input.pdfPermissionsBits)
	};
}
/**
* Walk the scope array; for every collab filter that consumes an
* identity field, verify the field is present. Throws `MissingIdentity`
* on the first mismatch — better to fail at open than to silently deny
* every mutation.
*
* Rules:
*   - `annotations:*:self` needs `identity.user_id`
*   - `annotations:*:group=X` needs `identity.groups` to include `X`
*     (matching the resolver's group-membership check)
*   - `annotations:*:createdBy=Y` is row-scoped, not caller-scoped, so
*     no identity field is required at open time.
*/
function assertIdentityForCollabScopes(scope, identity) {
	for (const s of scope) {
		const parsed = parseScope(s);
		if (parsed.kind !== "collab") continue;
		if (parsed.filter.kind === "self" && !identity.user_id) throw new MissingIdentity(s);
		if (parsed.filter.kind === "group") {
			if (!identity.groups?.includes(parsed.filter.groupId)) throw new MissingIdentity(s);
		}
	}
}
let warnedDefaultScope = false;
function warnDefaultScopeOnce() {
	if (warnedDefaultScope) return;
	warnedDefaultScope = true;
	console.warn("[engine-local] open() called without `scope`; defaulting to [\"*\"] (admin). Set `scope` explicitly to test realistic permissions matching your cloud deployment.");
}
//#endregion
//#region src/worker/IndexedPriorityHeap.ts
var IndexedPriorityHeap = class {
	constructor() {
		_defineProperty(this, "arr", []);
		_defineProperty(this, "nextSeq", 0);
	}
	get size() {
		return this.arr.length;
	}
	push(value, priority) {
		const handle = { index: this.arr.length };
		const entry = {
			value,
			priority,
			seq: this.nextSeq++,
			handle
		};
		this.arr.push(entry);
		this.siftUp(handle.index);
		return handle;
	}
	/**
	* Remove and return the highest-priority value (highest priority wins;
	* ties break in FIFO order).
	*/
	popMax() {
		if (this.arr.length === 0) return void 0;
		const top = this.arr[0];
		this.swapAndPop(0);
		top.handle.index = -1;
		return top.value;
	}
	remove(handle) {
		const i = handle.index;
		if (i < 0 || i >= this.arr.length) return false;
		handle.index = -1;
		if (i === this.arr.length - 1) {
			this.arr.pop();
			return true;
		}
		this.swapAndPop(i);
		return true;
	}
	swapAndPop(i) {
		const last = this.arr.pop();
		if (i < this.arr.length) {
			last.handle.index = i;
			this.arr[i] = last;
			const parent = this.parentOf(i);
			if (parent >= 0 && this.compare(i, parent) > 0) this.siftUp(i);
			else this.siftDown(i);
		}
	}
	siftUp(i) {
		while (i > 0) {
			const p = this.parentOf(i);
			if (this.compare(i, p) > 0) {
				this.swap(i, p);
				i = p;
			} else return;
		}
	}
	siftDown(i) {
		const n = this.arr.length;
		for (;;) {
			const l = i * 2 + 1;
			const r = l + 1;
			let best = i;
			if (l < n && this.compare(l, best) > 0) best = l;
			if (r < n && this.compare(r, best) > 0) best = r;
			if (best === i) return;
			this.swap(i, best);
			i = best;
		}
	}
	parentOf(i) {
		return (i + 1 >> 1) - 1;
	}
	/** > 0 when a should be above b in the heap. */
	compare(a, b) {
		const ea = this.arr[a];
		const eb = this.arr[b];
		if (ea.priority !== eb.priority) return ea.priority - eb.priority;
		return eb.seq - ea.seq;
	}
	swap(i, j) {
		const ai = this.arr[i];
		const aj = this.arr[j];
		this.arr[i] = aj;
		this.arr[j] = ai;
		ai.handle.index = j;
		aj.handle.index = i;
	}
};
//#endregion
//#region src/worker/jobIds.ts
let _nextJobId = 1;
/**
* Single process-wide job-id allocator shared by every producer that talks
* over a {@link Transport} — the WorkerQueue AND the boot pipeline's raw
* font-registration requests. One counter guarantees a boot-time jobId can
* never collide with a queued job's id on the same transport.
*/
function nextJobId() {
	return _nextJobId++;
}
//#endregion
//#region src/worker/WorkerQueue.ts
/**
* Priority queue with O(log n) abort-removes-pending semantics.
*
* - enqueue() returns an AbortablePromise. Calling .abort() before
*   dispatch removes the job from the heap (it never reaches the worker).
* - Calling .abort() after dispatch sends an AbortRequest to the worker.
*   The worker will reject when it next checks signal.aborted between
*   PDFium calls, then we deliver an AbortError to the caller.
*/
var WorkerQueue = class {
	constructor(transport, opts = {}) {
		this.transport = transport;
		_defineProperty(this, "pending", /* @__PURE__ */ new Map());
		_defineProperty(this, "inFlight", /* @__PURE__ */ new Map());
		_defineProperty(this, "heap", new IndexedPriorityHeap());
		_defineProperty(this, "unsubscribe", void 0);
		_defineProperty(this, "maxConcurrency", void 0);
		_defineProperty(this, "destroyed", false);
		this.maxConcurrency = Math.max(1, opts.concurrency ?? 1);
		this.unsubscribe = transport.onMessage((msg) => this.handleResponse(msg));
	}
	enqueue(spec, opts = {}) {
		if (this.destroyed) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.RuntimeUnavailable, "engine has been destroyed"));
		const jobId = nextJobId();
		const priority = opts.priority ?? Priority.MEDIUM;
		return new AbortablePromise((resolve, reject, _progress, signal) => {
			const handle = this.heap.push(jobId, priority);
			this.pending.set(jobId, {
				jobId,
				priority,
				buildPack: spec.buildPack,
				handle,
				resolve: (payload) => resolve(payload),
				reject
			});
			const onAbort = () => {
				const pending = this.pending.get(jobId);
				if (pending) {
					this.pending.delete(jobId);
					this.heap.remove(pending.handle);
					pending.reject(new AbortError(signal.reason));
					return;
				}
				const inFlight = this.inFlight.get(jobId);
				if (inFlight) {
					inFlight.aborted = true;
					inFlight.abortReason = signal.reason;
					this.transport.send(wirePack({
						kind: "abort",
						jobId
					}));
				}
			};
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			this.tick();
		});
	}
	tick() {
		if (this.destroyed) return;
		while (this.inFlight.size < this.maxConcurrency && this.heap.size > 0) {
			const jobId = this.heap.popMax();
			if (jobId === void 0) return;
			const pending = this.pending.get(jobId);
			if (!pending) continue;
			this.pending.delete(jobId);
			const pack = pending.buildPack(jobId);
			this.inFlight.set(jobId, {
				jobId,
				resolve: pending.resolve,
				reject: pending.reject,
				aborted: false,
				abortReason: void 0
			});
			this.transport.send(pack);
		}
	}
	handleResponse(msg) {
		const inFlight = this.inFlight.get(msg.jobId);
		if (!inFlight) return;
		this.inFlight.delete(msg.jobId);
		if (inFlight.aborted) inFlight.reject(new AbortError(inFlight.abortReason));
		else if (msg.kind === "resolve") inFlight.resolve(msg.result);
		else {
			const err = deserializeError(msg.error);
			if (err.code === EngineErrorCode.Aborted) inFlight.reject(new AbortError(err.message));
			else inFlight.reject(err);
		}
		this.tick();
	}
	async shutdown() {
		if (this.destroyed) return;
		this.destroyed = true;
		for (const pending of this.pending.values()) {
			this.heap.remove(pending.handle);
			pending.reject(new EngineError(EngineErrorCode.RuntimeUnavailable, "engine destroyed"));
		}
		this.pending.clear();
		try {
			const id = nextJobId();
			const settle = new Promise((resolveSettle) => {
				const off = this.transport.onMessage((m) => {
					if (m.jobId === id) {
						off();
						resolveSettle();
					}
				});
				this.transport.send(wirePack({
					kind: "shutdown",
					jobId: id
				}));
			});
			await Promise.race([settle, new Promise((r) => setTimeout(r, 50))]);
		} catch {}
		for (const inFlight of this.inFlight.values()) inFlight.reject(new EngineError(EngineErrorCode.RuntimeUnavailable, "engine destroyed"));
		this.inFlight.clear();
		this.unsubscribe();
		await this.transport.terminate();
	}
};
//#endregion
//#region src/LocalEngine.ts
/**
* Local engine: speaks the same Engine interface as @cloudpdf/engine
* but routes everything through a WorkerQueue + Transport (Web Worker or
* inline) backed by a WASM PDFium runtime.
*/
var LocalEngine = class LocalEngine {
	static fromTransport(opts) {
		return new LocalEngine(opts.transport, opts.concurrency ?? 1, opts.imageEncoder, opts.renderPolicy);
	}
	constructor(transport, concurrency, imageEncoder, renderPolicy) {
		this.transport = transport;
		_defineProperty(this, "queue", void 0);
		_defineProperty(this, "imageEncoder", void 0);
		_defineProperty(this, "renderPolicy", void 0);
		_defineProperty(this, "sessionId", `local:${generateUuid()}`);
		_defineProperty(this, "destroyed", false);
		_defineProperty(this, "fonts", void 0);
		this.queue = new WorkerQueue(transport, { concurrency });
		this.imageEncoder = imageEncoder ?? new BrowserImageEncoder();
		this.renderPolicy = renderPolicy ?? CONTINUOUS_RENDER_POLICY;
		this.fonts = new LocalFontService(this.queue);
	}
	/**
	* Start booting the backing transport (Worker spawn + WASM compile) without
	* doing any work. Lazy engines boot on first use anyway; calling this
	* overlaps the boot with app initialization instead of paying for it on the
	* first `open()`. No-op for transports that are live from construction.
	*/
	warmup() {
		if (this.destroyed) return;
		this.transport.start?.();
	}
	open(input, options) {
		if (this.destroyed) return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.RuntimeUnavailable, "engine destroyed"));
		if (input.kind === "bytes") return this.openBytes(input, options);
		if (input.kind === "layerBytes") return this.openLayerBytes(input, options);
		return AbortablePromise.rejectReason(new EngineError(EngineErrorCode.InvalidArg, `local engine only supports OpenInput.kind === 'bytes' or 'layerBytes' (got '${input.kind}')`));
	}
	openBytes(input, options) {
		const queue = this.queue;
		const password = options?.password ?? input.password ?? null;
		const buffer = toArrayBuffer(input.bytes);
		const docId = input.id;
		const submission = queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "open.fatMem",
			jobId,
			docId,
			bytes: buffer,
			password
		}, [buffer]) }, { priority: Priority.HIGH });
		return this.openResult(submission, options);
	}
	openLayerBytes(input, options) {
		const queue = this.queue;
		const password = options?.password ?? input.password ?? null;
		const docId = input.id;
		const baseKey = input.baseKey ?? input.id;
		const baseBytes = toArrayBuffer(input.baseBytes);
		const artifactBytes = input.layer?.kind === "artifact" ? toArrayBuffer(input.layer.bytes) : void 0;
		const layer = artifactBytes === void 0 ? { kind: "fresh" } : {
			kind: "artifact",
			bytes: artifactBytes
		};
		const transfer = artifactBytes ? [baseBytes, artifactBytes] : [baseBytes];
		const submission = queue.enqueue({ buildPack: (jobId) => wirePack({
			kind: "open.layerMemBase",
			jobId,
			docId,
			baseKey,
			baseBytes,
			layer,
			password
		}, transfer) }, { priority: Priority.HIGH });
		return this.openResult(submission, options);
	}
	openResult(submission, options) {
		const queue = this.queue;
		const imageEncoder = this.imageEncoder;
		return AbortablePromise.run(async (signal) => {
			const onAbort = () => submission.abort(signal.reason);
			if (signal.aborted) onAbort();
			else signal.addEventListener("abort", onAbort, { once: true });
			const payload = await submission;
			if (payload.tag !== "open") throw new EngineError(EngineErrorCode.WireFormat, `unexpected payload tag: ${payload.tag}`);
			const guard = new ScopeGuard(buildHandleScopeContext({
				scope: options?.scope,
				identity: options?.identity,
				pdfPermissionsBits: payload.security.pdfPermissionsBits
			}));
			return new LocalDocumentHandle(payload.docId, queue, imageEncoder, payload.security, guard, this.sessionId, this.renderPolicy);
		});
	}
	destroy() {
		if (this.destroyed) return AbortablePromise.resolveValue(void 0);
		this.destroyed = true;
		return AbortablePromise.run(async () => {
			await this.queue.shutdown();
			this.imageEncoder.destroy?.();
		});
	}
};
function toArrayBuffer(input) {
	if (input instanceof ArrayBuffer) return input;
	const copy = new ArrayBuffer(input.byteLength);
	new Uint8Array(copy).set(input);
	return copy;
}
//#endregion
//#region src/transport/BrowserWorkerTransport.ts
/**
* Latch a live worker's readiness handshake NOW, before anything else runs.
*
* A dedicated worker starts initializing the moment `new Worker()` executes,
* and its `ready` / `init-error` message is DROPPED if no listener is attached
* when it fires. Any code that accepts an already-created `Worker` but defers
* the transport (lazy engine boot) must therefore attach this latch
* synchronously at accept time, then hand the promise to
* {@link BrowserWorkerTransport.spawn} — otherwise a worker that finishes
* booting before the first engine operation would hang the handshake forever.
*/
function watchWorkerReady(worker) {
	return new Promise((resolve, reject) => {
		const settle = (done) => {
			worker.removeEventListener("message", onReady);
			worker.removeEventListener("error", onError);
			worker.removeEventListener("messageerror", onMessageError);
			done();
		};
		const onReady = (e) => {
			const data = e.data;
			if (!data || typeof data !== "object" || !("kind" in data)) return;
			if (data.kind === "ready") settle(resolve);
			else if (data.kind === "init-error") settle(() => reject(/* @__PURE__ */ new Error(`worker failed to initialize: ${data.error}`)));
		};
		const onError = (e) => {
			settle(() => reject(/* @__PURE__ */ new Error(`worker failed to start: ${e.message || "script error — bad worker URL, a Content-Security-Policy that blocks it, or a syntax error in the worker script"}`)));
		};
		const onMessageError = () => {
			settle(() => reject(/* @__PURE__ */ new Error("worker handshake message could not be deserialized")));
		};
		worker.addEventListener("message", onReady);
		worker.addEventListener("error", onError);
		worker.addEventListener("messageerror", onMessageError);
	});
}
/**
* Browser-only Web Worker transport. The worker is spawned externally and
* passed in here, which keeps this package independent of any specific
* bundler primitive (Vite's `?worker`, Webpack 5's `new URL(...)`, etc.).
*
* The worker entry is in src/worker/worker-entry.ts and consumers wire it
* up using their bundler's worker convention.
*/
var BrowserWorkerTransport = class BrowserWorkerTransport {
	/**
	* Wrap a worker and wait for its init handshake. Pass `ready` when the
	* worker existed before this call (see {@link watchWorkerReady}); omit it
	* only when the worker was created in the same tick, where no handshake
	* message can have fired yet.
	*/
	static async spawn(worker, ready) {
		const transport = new BrowserWorkerTransport(worker);
		await (ready ?? watchWorkerReady(worker));
		return transport;
	}
	constructor(worker) {
		this.worker = worker;
		_defineProperty(this, "listeners", /* @__PURE__ */ new Set());
		_defineProperty(this, "onMessageBound", void 0);
		this.onMessageBound = (e) => this.handleMessage(e);
		worker.addEventListener("message", this.onMessageBound);
	}
	send(pack) {
		this.worker.postMessage(pack.payload, pack.transfer);
	}
	onMessage(handler) {
		this.listeners.add(handler);
		return () => {
			this.listeners.delete(handler);
		};
	}
	async terminate() {
		this.worker.removeEventListener("message", this.onMessageBound);
		this.listeners.clear();
		this.worker.terminate();
	}
	handleMessage(e) {
		const data = e.data;
		if (!data || typeof data !== "object" || !("kind" in data)) return;
		if (data.kind === "ready" || data.kind === "init-error") return;
		for (const fn of this.listeners) try {
			fn(data);
		} catch {}
	}
};
//#endregion
//#region src/transport/InlineTransport.ts
/**
* Inline transport: runs the WorkerHost in the same JS thread as the queue.
* Dispatch is asynchronous (queueMicrotask) so callers always observe
* Promise-style ordering: synchronous code completes before any handler runs.
*
* Used by:
*   - Node demos and conformance tests (no Web Worker available)
*   - Browser fallback when the user opts out of workers
*   - Future: server's worker_thread will use a thin shim around this shape
*/
var InlineTransport = class {
	constructor(runtime) {
		_defineProperty(this, "host", void 0);
		_defineProperty(this, "listeners", /* @__PURE__ */ new Set());
		_defineProperty(this, "destroyed", false);
		this.host = new WorkerHost(runtime, (pack) => this.deliver(pack.payload));
	}
	send(pack) {
		if (this.destroyed) return;
		queueMicrotask(() => {
			if (this.destroyed) return;
			this.host.receive(pack.payload);
		});
	}
	onMessage(handler) {
		this.listeners.add(handler);
		return () => {
			this.listeners.delete(handler);
		};
	}
	async terminate() {
		this.destroyed = true;
		this.listeners.clear();
	}
	deliver(msg) {
		for (const fn of this.listeners) try {
			fn(msg);
		} catch {}
	}
};
//#endregion
//#region src/transport/LazyTransport.ts
var LazyTransport = class {
	constructor(boot, options = {}) {
		this.boot = boot;
		this.options = options;
		_defineProperty(this, "listeners", /* @__PURE__ */ new Set());
		_defineProperty(this, "pendingPacks", []);
		_defineProperty(this, "inner", void 0);
		_defineProperty(this, "innerUnsubscribe", void 0);
		_defineProperty(this, "bootPromise", void 0);
		_defineProperty(this, "bootError", void 0);
		_defineProperty(this, "terminated", false);
	}
	/**
	* Trigger the boot without sending anything — used by `Engine.warmup()` so
	* the WASM compile can start in parallel with app/plugin initialization.
	*
	* Idempotent. The returned promise settles when the boot settles but never
	* rejects; boot failures surface through the per-job reject responses.
	*/
	start() {
		if (this.bootPromise) return this.bootPromise;
		this.bootPromise = this.runBoot();
		return this.bootPromise;
	}
	send(pack) {
		if (this.terminated) return;
		if (this.bootError) {
			this.rejectPack(pack, this.bootError);
			return;
		}
		if (this.inner) {
			this.inner.send(pack);
			return;
		}
		if (this.bootPromise === void 0 && pack.payload.kind === "shutdown") {
			this.deliverAsync({
				kind: "resolve",
				jobId: pack.payload.jobId,
				result: { tag: "shutdown" }
			});
			return;
		}
		this.pendingPacks.push(pack);
		this.start();
	}
	onMessage(handler) {
		this.listeners.add(handler);
		return () => {
			this.listeners.delete(handler);
		};
	}
	async terminate() {
		if (this.terminated) return;
		this.terminated = true;
		this.pendingPacks.length = 0;
		if (this.bootPromise === void 0) {
			this.listeners.clear();
			this.options.onAbandon?.();
			return;
		}
		await this.bootPromise;
		this.listeners.clear();
		if (this.inner) {
			this.innerUnsubscribe?.();
			const inner = this.inner;
			this.inner = void 0;
			await inner.terminate();
		}
	}
	async runBoot() {
		try {
			const inner = await this.boot();
			if (this.terminated) {
				await inner.terminate().catch(() => {});
				return;
			}
			this.inner = inner;
			this.innerUnsubscribe = inner.onMessage((msg) => this.deliver(msg));
			for (const pack of this.pendingPacks.splice(0)) inner.send(pack);
		} catch (cause) {
			this.bootError = new EngineError(EngineErrorCode.RuntimeUnavailable, `engine failed to boot: ${cause instanceof Error ? cause.message : String(cause)}`, { cause });
			const failed = this.pendingPacks.splice(0);
			for (const pack of failed) this.rejectPack(pack, this.bootError);
		}
	}
	rejectPack(pack, error) {
		this.deliverAsync({
			kind: "reject",
			jobId: pack.payload.jobId,
			error: serializeError(error)
		});
	}
	deliverAsync(msg) {
		queueMicrotask(() => this.deliver(msg));
	}
	deliver(msg) {
		if (this.terminated) return;
		for (const fn of this.listeners) try {
			fn(msg);
		} catch {}
	}
};
//#endregion
//#region src/index.ts
/**
* @embedpdf/engine - Engine v3 local implementation.
*
* Public API:
*   localEngine(options?)               -> LocalEngine (Web Worker; boots lazily on first use)
*   createLocalEngine()                 -> LocalEngine using inline transport (Node, tests)
*   createLocalEngineWithWorker(worker) -> LocalEngine using a caller-supplied Web Worker
*
* All three construct SYNCHRONOUSLY and allocate nothing until the first
* operation (or `engine.warmup()`): readiness lives inside {@link LazyTransport},
* so no caller ever awaits engine construction.
*/
/**
* Create a LocalEngine that runs PDFium inline in the current thread.
* Suitable for Node, tests, and as a worker-less browser fallback.
*
* Synchronous: the runtime (WASM compile / native load) boots lazily on the
* first operation, inside the engine's {@link LazyTransport}.
*/
function createLocalEngine(opts = {}) {
	const transport = new LazyTransport(async () => new InlineTransport(await createPdfRuntime(opts.runtime ?? {})));
	return LocalEngine.fromTransport({
		transport,
		concurrency: opts.concurrency,
		imageEncoder: opts.imageEncoder,
		renderPolicy: opts.renderPolicy
	});
}
/**
* Create a LocalEngine that talks to a caller-supplied Web Worker. The worker
* must be wired up to engine-local's worker-entry (see src/worker/worker-entry.ts).
*
* Synchronous. Pass a `() => Worker` thunk to defer even the Worker allocation
* until the engine first boots (first operation or `warmup()`); passing a live
* `Worker` is also fine — its init handshake is latched immediately (see
* {@link watchWorkerReady}) so a worker that finishes booting before the first
* engine operation cannot hang the lazy boot.
*/
function createLocalEngineWithWorker(opts) {
	const boot = workerBoot(opts.worker, opts);
	const transport = new LazyTransport(boot.spawn, boot.lazyOptions);
	return LocalEngine.fromTransport({
		transport,
		concurrency: opts.concurrency,
		imageEncoder: opts.imageEncoder,
		renderPolicy: opts.renderPolicy
	});
}
/**
* Resolve a `worker` option into a boot-time transport factory plus
* LazyTransport options — the one place the delivery asymmetries are handled:
*
*   - LIVE `Worker`: it began initializing at `new Worker()`, and its
*     `ready`/`init-error` message is dropped if nothing is listening when it
*     fires. So the init message is posted and the handshake latched HERE,
*     synchronously at engine construction, and the latch is what the
*     deferred spawn awaits. The abandon hook terminates the worker if the
*     engine is destroyed without ever booting (the boot factory never ran,
*     so nobody else would).
*   - Thunk / URL / default inline: nothing exists until boot, so nothing can
*     race and nothing needs reclaiming.
*
* The wasm source rides the same decision: only the inline blob worker (which
* has no meaningful location of its own) receives the sibling-first default —
* the bundler-resolved asset URL with the version-pinned CDN as a
* fetch-failure-only fallback (see resolveInlineWasmSource); every other
* delivery self-resolves `embedpdf.wasm` as a sibling of the worker script when
* no explicit source is configured.
*/
function workerBoot(source, wasmOptions) {
	const delivery = source ?? "inline";
	if (typeof delivery === "object" && delivery !== null) {
		postWorkerInit(delivery, resolveWasmSource(wasmOptions));
		const ready = watchWorkerReady(delivery);
		ready.catch(() => {});
		return {
			spawn: () => BrowserWorkerTransport.spawn(delivery, ready),
			lazyOptions: { onAbandon: () => delivery.terminate() }
		};
	}
	return {
		spawn: async () => {
			const wasm = delivery === "inline" ? await resolveInlineWasmSource(wasmOptions) : resolveWasmSource(wasmOptions);
			const spawned = await createEngineWorker(delivery);
			postWorkerInit(spawned.worker, wasm);
			try {
				const transport = await BrowserWorkerTransport.spawn(spawned.worker);
				spawned.dispose();
				return transport;
			} catch (error) {
				spawned.dispose();
				spawned.worker.terminate();
				throw error;
			}
		},
		lazyOptions: {}
	};
}
function postWorkerInit(worker, wasm) {
	const init = {
		kind: "init",
		wasmUrl: wasm.wasmUrl
	};
	if (wasm.fallbackWasmUrl) init.fallbackWasmUrl = wasm.fallbackWasmUrl;
	if (wasm.wasmBinary) init.wasmBinary = wasm.wasmBinary;
	worker.postMessage(init, wasm.wasmBinary ? [wasm.wasmBinary] : []);
}
/**
* Create the worker for a non-live delivery. The default (`'inline'`) path
* lazily imports the worker source string — its own module, so bundlers emit
* it as a separate chunk that is only ever downloaded when this line runs —
* and spawns it from a blob URL (revoked once the handshake settles).
*/
async function createEngineWorker(delivery) {
	if (typeof delivery === "function") return {
		worker: delivery(),
		dispose: () => {}
	};
	if (typeof delivery === "string" && delivery !== "inline") return {
		worker: new Worker(toAbsoluteUrl(delivery), { type: "module" }),
		dispose: () => {}
	};
	let source;
	try {
		source = (await import("./embedpdf-worker.source-C7N-dlWS.js")).default;
	} catch (cause) {
		throw new Error("[embedpdf] could not load the inline engine worker module (@embedpdf/engine/worker-source). If your bundler cannot resolve it, pass a worker explicitly: `localEngine({ worker: () => new Worker(...) })`.", { cause });
	}
	const blobUrl = URL.createObjectURL(new Blob([source], { type: "text/javascript" }));
	try {
		return {
			worker: new Worker(blobUrl, { type: "module" }),
			dispose: () => URL.revokeObjectURL(blobUrl)
		};
	} catch (cause) {
		URL.revokeObjectURL(blobUrl);
		throw new Error("[embedpdf] could not create the engine worker from a blob: URL — most likely your Content-Security-Policy omits `worker-src blob:`. Self-host the worker instead: copy @embedpdf/engine's workers/embedpdf-worker.js and embedpdf.wasm into one served directory and pass `worker: '/that/directory/embedpdf-worker.js'` to localEngine(). See https://www.embedpdf.com/docs/self-hosting", { cause });
	}
}
/**
* Create a local (PDFium-in-a-Worker) {@link LocalEngine}.
*
* SYNCHRONOUS AND CHEAP: the returned object is a fully usable {@link Engine},
* but it allocates nothing — no Worker, no WASM — until the first operation
* (or an explicit `engine.warmup()`). That makes it safe to create at module
* scope, including on a server (Next/Nuxt SSR): nothing browser-specific runs
* until someone actually opens a document in the browser.
*
* Configured fonts are guaranteed to be registered on the worker before any
* other work runs: the boot pipeline fetches font URLs in parallel with the
* worker spawn, registers everything, and only then releases queued jobs.
* Dynamic registration later via `engine.fonts.register()` also just works.
*
* Ownership: the engine is yours — call `engine.destroy()` when you are done
* (for a module-scope singleton backing your whole app, that is usually
* never). To let a `<Viewer>` own the lifetime instead, pass a thunk:
* `engine={() => localEngine()}` — the viewer creates it on mount and
* destroys it on unmount.
*
* ```ts
* const engine = localEngine({
*   fallbackFonts: [{ key: 'noto-cjk', url: '/fonts/NotoSansCJK.ttf' }],
* });
* <Viewer engine={engine} plugins={[stagePlugin(), renderPlugin()]} />
* ```
*/
function localEngine(options = {}) {
	const boot = workerBoot(options.worker, options);
	const transport = new LazyTransport(async () => {
		const [spawned, resolvedFonts] = await Promise.allSettled([boot.spawn(), resolveRecipeFonts(options)]);
		if (spawned.status === "rejected") throw spawned.reason;
		const inner = spawned.value;
		try {
			if (resolvedFonts.status === "rejected") throw resolvedFonts.reason;
			await registerBootFonts(inner, engine.fonts, resolvedFonts.value);
			return inner;
		} catch (error) {
			await inner.terminate().catch(() => {});
			throw error;
		}
	}, boot.lazyOptions);
	const engine = LocalEngine.fromTransport({
		transport,
		concurrency: options.concurrency,
		imageEncoder: options.imageEncoder ?? (options.encoderWorker !== void 0 ? new BrowserImageEncoder({ worker: options.encoderWorker }) : void 0),
		renderPolicy: options.renderPolicy
	});
	return engine;
}
/** Resolve every configured font's bytes (fetching `url` entries) in declared
*  order: plain fonts first, then fallback fonts — fallback precedence is
*  registration order (first font covering a missing glyph wins). */
async function resolveRecipeFonts(options) {
	const entries = [...(options.fonts ?? []).map((raw) => ({
		raw,
		fallback: false
	})), ...(options.fallbackFonts ?? []).map((raw) => ({
		raw,
		fallback: true
	}))];
	return Promise.all(entries.map(async ({ raw, fallback }) => ({
		spec: await toFontSpec(raw),
		fallback
	})));
}
/**
* Register boot-config fonts by RAW transport send, bypassing the WorkerQueue.
*
* The bypass is load-bearing, not an optimization: during boot the queue's
* concurrency slot may already be held by a buffered user `open()`, so a
* queued font job could never dispatch — and the ordering guarantee ("fonts
* before any user job") requires these packs to hit the worker while user
* packs are still buffered in the LazyTransport. The main-thread FontService
* registry is seeded afterwards so `list()` / idempotent `register()` /
* `replay()` stay consistent.
*/
async function registerBootFonts(transport, fontService, fonts) {
	for (const { spec, fallback } of fonts) {
		fontService.seedRegistered(spec, { fallback });
		const bytes = toStandaloneArrayBuffer(spec.data);
		await requestOverTransport(transport, (jobId) => wirePack({
			kind: "fonts.register",
			jobId,
			fontKey: spec.key,
			familyName: spec.familyName ?? "",
			weight: spec.weight ?? 0,
			italic: spec.italic === void 0 ? -1 : spec.italic ? 1 : 0,
			data: bytes
		}, [bytes]));
		if (fallback) await requestOverTransport(transport, (jobId) => wirePack({
			kind: "fonts.addFallback",
			jobId,
			fontKey: spec.key
		}));
	}
}
/** One raw request/response round-trip over a live transport (the same
*  settle-listener pattern WorkerQueue.shutdown uses). */
function requestOverTransport(transport, buildPack) {
	const jobId = nextJobId();
	return new Promise((resolve, reject) => {
		const off = transport.onMessage((msg) => {
			if (msg.jobId !== jobId) return;
			off();
			if (msg.kind === "resolve") resolve(msg.result);
			else reject(deserializeError(msg.error));
		});
		transport.send(buildPack(jobId));
	});
}
async function toFontSpec(spec) {
	if (spec.data == null === (spec.url == null)) throw new Error(`[embedpdf] localEngine: font "${spec.key}" must set exactly one of \`data\` or \`url\``);
	const data = spec.data ?? await fetchFontBytes(spec.url);
	return {
		key: spec.key,
		familyName: spec.familyName,
		weight: spec.weight,
		italic: spec.italic,
		data
	};
}
async function fetchFontBytes(url) {
	const response = await fetch(url);
	if (!response.ok) throw new Error(`[embedpdf] localEngine: failed to fetch font ${url}: ${response.status}`);
	return new Uint8Array(await response.arrayBuffer());
}
function toStandaloneArrayBuffer(input) {
	if (input instanceof ArrayBuffer) return input.slice(0);
	const copy = new ArrayBuffer(input.byteLength);
	new Uint8Array(copy).set(input);
	return copy;
}
//#endregion
export { BrowserImageEncoder, BrowserWorkerTransport, DEFAULT_WASM_URL, InlineTransport, LazyTransport, LocalDocumentAnnotationsService, LocalDocumentHandle, LocalDocumentPagesService, LocalEngine, LocalFontService, LocalPageAnnotationsService, LocalPageGeometryService, LocalPageHandle, LocalPageRenderService, Priority, createLocalEngine, createLocalEngineWithWorker, localEngine, resolveInlineWasmSource, resolveWasmSource };

//# sourceMappingURL=index.js.map